import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  getTotalPriceText,
  twoDecimalFormatter,
  ISummaryLineItem,
  emailRegex,
  phoneRegex,
  roundToTwoDecimals,
  IconName,
  getCurrencySymbol,
  GENERIC_ERR_TITLE,
  GENERIC_ERR_SUBTITLE,
  IFlightSummaryData,
  truncateToTwoDecimals,
  getCheckoutCreditBreakdown,
  TEST_CARD_LAST_FOURS,
  paymentTextConstants,
  getCreditBreakdownAvailableByProduct,
  getCreditsAvailableByProduct,
  getCreditBreakdownIneligibleByProduct,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
  getDateTimeWithFormat,
} from "halifax";
import {
  CallState,
  Currency,
  RewardsPrice,
  FiatPrice,
  CreditCard,
  PassengerError,
  PaymentAmountEnum,
  PaymentSplitRequest,
  PaymentSplitRequestEnum,
  SchedulePriceQuoteRequest,
  IsEligible,
  PaymentVerifyResultEnum,
  ReviewFlightDetailsProperties,
  FareBrand,
  CompleteBuyAirProperties,
  PriceDropViewedProperties,
  PriceDropProtectionEnum,
  RewardsAccountMinimumRequirementState,
  getRewardsAccountMinimumRequirementState,
  getHopperFareRatingName,
  SeatMapResponseEnum,
  PostQuoteAncillariesEnum,
  FlightBookType,
  PostQuoteAncillaries,
  PaymentType,
  TypeOfPaymentEnum,
  SplitPaymentType,
  RewardsPaymentType,
  UserCardPaymentType,
  ErrorTitles,
  ErrorModalType,
  PriceDropProtection,
  ProtectionId,
  PassengersForFareRequest,
  ProtectionPolicyIdEnum,
  ProtectionIdEnum,
  PriceQuoteAirProperties,
  CfarAttachProperties,
  PaymentV2Enum,
  AncillaryOfferEnum,
  ITrackingProperties,
  PaymentError as LegacyPaymentError,
  GeneralPurchaseErrorEnum,
  PurchaseError as RedmondPurchaseError,
  UncategorizedPurchaseError,
  PaymentErrorEnum,
  PaymentPurchaseErrorEnum,
  TravelItineraryEnum,
  BookedFlight,
  PartialRebookedItineraryDpExerciseItineraryFacts,
  PartialRebookedItineraryDpDisruptionPolicyExercisedFacts,
  PartialRebookedItineraryDpRebookingCompleteBuyFacts,
  getCoverageAndPremiumFromDisruptionAncillaries,
  getTripSegmentsFromItinerarySegments,
  getPlusDaysFromItinerarySegments,
  getStopsFromSegments,
  getSlicesFromTravelItinerary,
  PartialPassengerDpExerciseFlightsListFacts,
  TripCategory,
  getPriceBucketValueFromDealness,
  DullesUta,
  CorpPriceQuoteData,
  containsTravelFusionFareSegmentBrand,
  CustomerAccountRole,
  RewardsAccount,
  IPerson,
  ICorpPerson,
  RestrictedToProductsEnum,
  InsuranceBaseProperties,
} from "redmond";
import {
  PassengerAncillaryPricing,
  PassengerTripPricing,
  PersonCredits,
  PriceQuoteData,
  Prices,
  FlightError,
} from "@b2bportal/air-booking-api";
import { IsEligible as ShoppingIsEligible } from "@b2bportal/air-shopping-api";
import {
  Ancillary,
  AncillaryOpaqueValue,
  ErrorCode,
  Payment,
  PaymentError,
  PaymentOpaqueValue,
  Product,
  ProductError,
  PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { cloneDeep } from "lodash";
import queryStringParser from "query-string";
import {
  CardDeclined,
  HtsAncillaryId,
  HtsAncillaryIdType,
} from "redmond/apis/tysons/payment-machine";
import { isCorpTenant } from "@capone/common";
import {
  CfarOfferType,
  DisruptionOfferType,
} from "redmond/apis/tysons/ancillaries";
import { IStoreState } from "../../../../reducers/types";
import {
  getAgentEmail,
  getEligibleRewardsAccountWithLargestValue,
  getRewardsAccounts,
  getRewardsAccountWithLargestValue,
  getSelectedAccount,
} from "../../../rewards/reducer";
import { resolvePayFlexCardProduct } from "./payFlexCardProduct";
import { resolveRewardsAllocations } from "./rewardsAllocation";
import { buildMultiRewardsPaymentEntries } from "./multiRewardsPayment";
import { resolveMultiRewardsTrackingSummary } from "./multiRewardsTracking";
import {
  getViewedTripSummaryProperties,
  shopPricingInfoSelector,
  selectedTripDetailsSelector,
  selectedFareDetailsSelector,
  selectedTripSelector,
  tripDetailsByIdSelector,
  getSelectedFlightIndex,
  selectedCfarIdSelector,
  getCfarAttachProperties,
  selectedDisruptionProtectionIdSelector,
  selectedDisruptionProtectionOfferSelector,
  dpOfferPropertiesSelector,
  isDpOptionSelectionCompleteSelector,
  isDisruptionProtectionAvailableSelector,
  isCfarOptionSelectionCompleteSelector,
  isCfarAvailableSelector,
  selectedChfarIdSelector,
  isFlightBookWithAncillariesActiveSelector,
  hasSelectedRefundableFareSelector,
  flightsByFlightShopTypeSelector,
  predictionSelector,
  getFlightShopEntryPoint,
  selectedDiscountedCfarOfferPricesSelector,
  cfarDiscountPropertiesSelector,
  getAllowRewardsWithPolicy,
  getShopExperiments,
  SelectedAncillaries,
  getCorporateTravel,
} from "../../../shop/reducer/selectors";
import { getEffectiveCorporateTravel as getEffectiveCorporateTravelPure } from "../../components/capone-corporate/getEffectiveCorporateTravel";
import {
  DO_NOT_APPLY_OPTION_KEY,
  ISelectedMulticityTrip,
  ISelectedTrip,
} from "../../../shop/reducer";
import {
  getBookedAirlineCodes,
  getMatchedBusinessLoyaltyPrograms,
  hasRelevantFrequentFlyer,
} from "@capone/common";
import {
  singleFlightItineraryPassengersSelector,
  singleFlightItineraryIdSelector,
  contextFromRebookSummarySelector,
  singleFlightItinerarySelector,
  singleFlightItineraryReturnSliceSelector,
  isSelectingReturnFlightSelector,
  dpExercisePassengerPropertiesSelector,
  isRefundableFaresEnabledSelector,
  getPriceDropRefundTypeSelector,
} from "../../../ancillary/reducer/selectors";
import * as textConstants from "./textConstants";
import { getErrorTitlesFromPaymentError } from "../utils/getErrorTitles";
import {
  getCfarFee,
  getDisruptionProtectionFee,
  getSubtotalWithAncillaries,
  roundRewards,
} from "../utils/pricingHelpers";
import { getTripCategory } from "../../../search/reducer";
import {
  getMulticityParamsFromTripDetails,
  getIsCorporateRateBooking,
  getSliceIndex,
} from "../../../../utils/flights";
import {
  AVAILABLE,
  CONTROL,
  CORP_HIDE_PRICE_DROP_EXPERIMENT,
  HOTEL_CROSS_SELL_V3_EXPERIMENT,
} from "../../../../context/experiments";
import { getPotentialCrossSellOffers } from "../../../cross-sell/reducer/selectors";
import { config } from "../../../../api/config";
import { selectChfarExerciseContractId } from "../../../change-for-any-reason/reducers/exercise/selectors";
import { getCreditBreakdown } from "../../../travel-wallet/reducer";

export const getUserPassengers = (state: IStoreState) =>
  state.flightBook.userPassengers;

export const getUserSelectedPassengerIds = (state: IStoreState) =>
  state.flightBook.userSelectedPassengerIds;

export const getUserSelectedLapInfantIds = (state: IStoreState) =>
  state.flightBook.userSelectedLapInfantIds;

export const getUserSelectedTravelersList = (state: IStoreState) =>
  state.flightBook.userSelectedTravelersList;

export const getUserPassengerCallState = (state: IStoreState) =>
  state.flightBook.userPassengerCallState;

export const getConfirmationEmail = (state: IStoreState) =>
  state.flightBook.confirmationEmailAddress;

export const getConfirmationPhoneNumber = (state: IStoreState) =>
  state.flightBook.confirmationPhoneNumber;

export const getUserTcpaConsentPhoneNumber = (state: IStoreState) =>
  state.flightBook.tcpaConsentPhoneNumber;

export const getPaymentMethods = (state: IStoreState) =>
  state.flightBook.paymentMethods;

export const getPaymentMethod = (state: IStoreState) =>
  state.flightBook.paymentMethod;

export const getSelectedPaymentMethodId = (state: IStoreState) =>
  state.flightBook.selectedPaymentMethodId;

export const getListPaymentMethodsCallState = (state: IStoreState) =>
  state.flightBook.listPaymentMethodCallState;

export const getDeletePaymentMethodCallState = (state: IStoreState) =>
  state.flightBook.deletePaymentMethodCallState;

export const getFetchPaymentMethodCallState = (state: IStoreState) =>
  state.flightBook.fetchPaymentMethodCallState;

export const getVerifyPaymentMethodCallState = (state: IStoreState) =>
  state.flightBook.verifyPaymentMethodCallState;

export const getSession = (state: IStoreState) => state.flightBook.session;

export const getSessionCallState = (state: IStoreState) =>
  state.flightBook.sessionCallState;

export const getPriceDifferenceAcknowledged = (state: IStoreState) =>
  state.flightBook.priceDifferenceAcknowledged;

export const getPriceQuote = (state: IStoreState) =>
  state.flightBook.priceQuote;

export const getPriceQuoteErrors = (state: IStoreState) =>
  state.flightBook.priceQuoteErrors;

export const getSchedulePriceQuoteError = (state: IStoreState) =>
  state.flightBook.schedulePriceQuoteError;

export const getSchedulePaymentError = (state: IStoreState) =>
  state.flightBook.schedulePaymentError;

export const getPollPriceQuoteCallState = (state: IStoreState) =>
  state.flightBook.pollPriceQuoteCallState;

export const getSchedulePriceQuoteCallState = (state: IStoreState) =>
  state.flightBook.schedulePriceQuoteCallState;

export const getInfantOnlyError = (state: IStoreState) =>
  state.flightBook.infantOnlyError;

export const getPassengersValid = (state: IStoreState) =>
  state.flightBook.passengersValid;

export const getPassengersValidErrors = (state: IStoreState) =>
  state.flightBook.passengerErrors;

export const getPassengersValidCallState = (state: IStoreState) =>
  state.flightBook.validatePassengersCallState;

export const getTripPricing = (state: IStoreState) =>
  state.flightBook.tripPricing;

export const getTripPricingErrors = (state: IStoreState) =>
  state.flightBook.tripPricingErrors;

export const getTripPricingCallState = (state: IStoreState) =>
  state.flightBook.tripPricingCallState;

export const getFinalizedItinerary = (state: IStoreState) =>
  state.flightBook.finalizedItinerary;

export const getFinalizedUserEmail = (state: IStoreState) =>
  state.flightBook.finalizedUserEmail;

export const getFinalizedErrors = (state: IStoreState) =>
  state.flightBook.finalizedErrors;

export const getFinalizedCallState = (state: IStoreState) =>
  state.flightBook.finalizedCallState;

export const getChargeAgentBookingFeeCallState = (state: IStoreState) =>
  state.flightBook.chargeAgentBookingFeeCallState;

export const getFlightBookType = (state: IStoreState) =>
  state.flightBook.flightBookType;

export const getIsChfarExerciseBooking = createSelector(
  getFlightBookType,
  (flightBookType) =>
    flightBookType === FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE
);

export const getSetFlightBookQueryParamsCallState = (state: IStoreState) =>
  state.flightBook.setFlightBookQueryParamsCallState;

export const getFlightBookPriceDropProtection = (state: IStoreState) => {
  if (
    state.flightShop.experiments?.[CORP_HIDE_PRICE_DROP_EXPERIMENT] ===
    AVAILABLE
  ) {
    return null;
  }
  return state.flightBook.priceDropProtection;
};

export const getFetchPriceDropEligibilityCallState = (state: IStoreState) =>
  state.flightBook.fetchPriceDropEligibilityCallState;

export const getBestOfferOverall = (state: IStoreState) =>
  state.flightBook.bestOfferOverall;

export const getOffers = (state: IStoreState) =>
  filterOutSpendAndGetOffers(state.flightBook.offers ?? []);

export const getOfferToApply = (state: IStoreState) => {
  const offer = state.flightBook.offerToApply;
  // Don't return spend-and-get offers
  if (offer && isSpendAndGetOffer(offer.descriptions)) return undefined;
  return offer;
};

export const getCredit = createSelector(
  [(state: IStoreState) => state.flightBook.credit],
  (credit) => {
    if (credit) {
      return {
        ...credit,
        amount: {
          ...credit.amount,
          amount: truncateToTwoDecimals(credit.amount.amount),
        },
      } satisfies typeof credit;
    }
    return credit;
  }
);

export const getApplicableCredit = createSelector(getCredit, (credit) =>
  credit
    ? getCreditsAvailableByProduct(RestrictedToProductsEnum.Air, credit)
    : credit
);

export const getApplicableCreditsBreakdown = createSelector(
  getCreditBreakdown,
  (creditBreakdown) =>
    getCreditBreakdownAvailableByProduct(
      RestrictedToProductsEnum.Air,
      creditBreakdown
    )
);

export const getIneligibleCreditsBreakdown = createSelector(
  getCreditBreakdown,
  (creditBreakdown) =>
    getCreditBreakdownIneligibleByProduct(
      RestrictedToProductsEnum.Air,
      creditBreakdown
    )
);

export const getCreditToApply = createSelector(
  [(state: IStoreState) => state.flightBook.creditToApply],
  (credit) => {
    if (credit) {
      return {
        ...credit,
        amount: {
          ...credit.amount,
          amount: truncateToTwoDecimals(credit.amount.amount),
        },
      } satisfies typeof credit;
    }
    return credit;
  }
);

export const getUserDeselectedCredits = (state: IStoreState) =>
  state.flightBook.userDeselectedCredits;

export const getUserDeselectedOffers = (state: IStoreState) =>
  state.flightBook.userDeselectedOffers;

export const getUserSelectedRewardsManually = (state: IStoreState) =>
  state.flightBook.userSelectedRewardsManually;

export const getCorporateInfo = (state: IStoreState) =>
  state.flightRewards.corporateInfo;

export const getTripPurpose = (state: IStoreState) =>
  state.flightBook.tripPurpose;

export const getTravelWalletItemsToApply = createSelector(
  getOfferToApply,
  getCreditToApply,
  (offerToApply, creditToApply) => ({
    offerToApply,
    creditToApply,
  })
);

export const getTravelOfferProperties = createSelector(
  getBestOfferOverall,
  getOfferToApply,
  getOffers,
  (bestOverallOffer, offerToApply, offers): ITrackingProperties => ({
    properties: {
      ...bestOverallOffer?.trackingPropertiesV2?.properties,
      ...offerToApply?.trackingPropertiesV2?.properties,
      has_offer: !!bestOverallOffer,
      offer_count: offers?.length || 0,
    },
    encryptedProperties: [
      bestOverallOffer?.trackingPropertiesV2?.encryptedProperties ?? "",
      offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
    ],
  })
);

export const getFetchApplicableTravelWalletItemsCallState = (
  state: IStoreState
) => state.flightBook.fetchApplicableTravelWalletItemsCallState;

export const getFetchTravelWalletSummaryCallState = (state: IStoreState) =>
  state.flightBook.fetchTravelWalletSummaryCallState;

export const getConfirmationContactInfo = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  (email, phoneNumber) => ({
    email: email ?? "",
    phoneNumber: phoneNumber ?? "",
  })
);

export const getIsWaitingPriceQuote = createSelector(
  getPollPriceQuoteCallState,
  getSchedulePriceQuoteCallState,
  (pollPriceQuoteCallState, shedulePriceQuoteCallState) =>
    pollPriceQuoteCallState === CallState.InProcess ||
    shedulePriceQuoteCallState === CallState.InProcess
);

export const isRefetchingPriceQuoteSelector = createSelector(
  getPriceQuote,
  getTripPricing,
  getIsWaitingPriceQuote,
  isFlightBookWithAncillariesActiveSelector,
  (
    priceQuote,
    tripPricing,
    isWaitingPriceQuote,
    isFlightBookWithAncillariesActive
  ): boolean =>
    /*
        note: isFlightBookWithAncillariesActive is controlled by the feature flag for the https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting, and it can only return
        true when the flight-book variant is enabled; the condition `isWaitingPriceQuote && !!priceQuote` can only be satisfied on desktop,
        because it's created through using the `preserveQuote` option of scheduleQuote, which is only used in the ancillary selection component.
      */
    isFlightBookWithAncillariesActive &&
    isWaitingPriceQuote &&
    !!priceQuote &&
    !!tripPricing
);

/*
  note: in the flight-book variant of https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting, the persisted price quote needs to include ancillary options
  while refetching a new quote; by overwriting itinerary.sellingPricing with tripPricing, latest ancillary options will be included
*/
export const getPriceQuoteWithUpdatedAncillary = createSelector(
  getPriceQuote,
  getTripPricing,
  isRefetchingPriceQuoteSelector,
  (
    priceQuote,
    tripPricing,
    isRefetchingPriceQuote
  ): PriceQuoteData | CorpPriceQuoteData | null => {
    if (isRefetchingPriceQuote && !!priceQuote && !!tripPricing) {
      const priceQuoteWithUpdatedAncillary = cloneDeep(priceQuote);

      // handle pricingByPassenger
      const tripPricingPassengerMap = tripPricing.pricingByPassenger.reduce(
        (map, pricing) => {
          map[pricing.person.id] = pricing;
          return map;
        },
        {} as { [key in string]: PassengerTripPricing }
      );
      priceQuoteWithUpdatedAncillary.itinerary.sellingPricing.pricingByPassenger.forEach(
        (pricing) => {
          const chfarAncillary: PassengerAncillaryPricing | undefined =
            tripPricing.totalPricing.chfar
              ? { kind: "Chfar", premium: tripPricing.totalPricing.chfar }
              : undefined;

          const { id } = pricing.person;
          // update the person in priceQuote with the same person in tripPrice
          if (tripPricingPassengerMap[id]) {
            // ChFAR is not returned in the response's ancillaries key, but in its own key.
            pricing.ancillaries = [
              ...tripPricingPassengerMap[id].ancillaries,
              ...(chfarAncillary ? [chfarAncillary] : []),
            ];

            pricing.total = getSubtotalWithAncillaries(
              pricing.subtotal,
              tripPricingPassengerMap[id].ancillaries
            );
          }
        }
      );

      // handle totalPricing
      priceQuoteWithUpdatedAncillary.itinerary.sellingPricing.totalPricing.ancillaries =
        tripPricing.totalPricing.ancillaries;

      priceQuoteWithUpdatedAncillary.itinerary.sellingPricing.totalPricing.total =
        getSubtotalWithAncillaries(
          priceQuoteWithUpdatedAncillary.itinerary.sellingPricing.totalPricing
            .subtotal,
          tripPricing.totalPricing.ancillaries
        );

      return priceQuoteWithUpdatedAncillary;
    }
    return priceQuote;
  }
);

export const getSeatMapAvailability = createSelector(
  getPriceQuoteWithUpdatedAncillary,
  (priceQuote) =>
    priceQuote?.seatMap &&
      priceQuote?.seatMap.SeatMapResponse ===
      SeatMapResponseEnum.SeatMapUnavailable
      ? false
      : !(priceQuote && !priceQuote.seatMap)
);

export const getSeatSelectionAvailability = createSelector(
  getPriceQuoteWithUpdatedAncillary,
  (priceQuote) => priceQuote?.seatMap?.SeatMapResponse
);

export const getSeatMapHtml = (state: IStoreState) =>
  state.flightBook.seatMapHtml;

export const getCheapestSeat = (state: IStoreState) =>
  state.flightBook.cheapestSeat;

export const getSeatMapLoading = (state: IStoreState) =>
  state.flightBook.seatMapLoading;
export const getSkipSeatSelection = (state: IStoreState) =>
  state.flightBook.skipSeatSelection;

export const getSelectedSeats = (state: IStoreState) =>
  state.flightBook.selectedSeats;
export const getPriceDifference = createSelector(
  getPriceQuoteWithUpdatedAncillary,
  getTripPricing,
  getPriceDifferenceAcknowledged,
  isFlightBookWithAncillariesActiveSelector,
  (
    priceQuote,
    tripPricing,
    acknowledged,
    isFlightBookWithAncillariesActive
  ): {
    hasDifference: boolean;
    isIncrease: boolean;
    predictedTotal: number;
    priceQuoteTotal: number;
    amount?: string;
    amountPerTraveler?: string;
    currency?: string;
  } => {
    if (!priceQuote || !tripPricing || acknowledged) {
      return {
        hasDifference: false,
        isIncrease: false,
        predictedTotal: 0,
        priceQuoteTotal: 0,
      };
    }

    let predictedTotal: number;
    let priceQuoteTotal: number;
    let predictedTotalPerTraveler: number;
    let priceQuoteTotalPerTraveler: number;
    /*
      note: comparing total.fiat is incorrect in the ancillary attachment variant of the flights checkout flow, because when
      the ancillaries are included in the calculation, it causes the modal to show up whenever the user chooses to include any ancillary;
      it's the flight-book variant of https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting, which currently isn't active
    */
    if (isFlightBookWithAncillariesActive) {
      predictedTotal = tripPricing.totalPricing.baseWithoutMargin.fiat.value;
      priceQuoteTotal =
        priceQuote.itinerary.sellingPricing.totalPricing.baseWithoutMargin.fiat
          .value;
      predictedTotalPerTraveler =
        tripPricing.pricingByPassenger[0]?.baseWithoutMargin.fiat.value;
      priceQuoteTotalPerTraveler =
        priceQuote.itinerary.sellingPricing.pricingByPassenger[0]
          ?.baseWithoutMargin.fiat.value;
    } else {
      predictedTotal = tripPricing.totalPricing.total.fiat.value;
      priceQuoteTotal =
        priceQuote.itinerary.sellingPricing.totalPricing.total.fiat.value;
      predictedTotalPerTraveler =
        tripPricing.pricingByPassenger[0]?.total.fiat.value;
      // note: pricingByPassenger.total does not seem to include ancillary prices - not sure if that's intended
      priceQuoteTotalPerTraveler = roundToTwoDecimals(
        priceQuote.itinerary.sellingPricing.pricingByPassenger[0]?.subtotal.fiat
          .value +
        priceQuote.itinerary.sellingPricing.pricingByPassenger[0]?.ancillaries.reduce(
          (sum, ancillary) => sum + ancillary.premium.fiat.value,
          0
        )
      );
    }

    const difference = Math.abs(priceQuoteTotal - predictedTotal);
    const amount = difference.toFixed();
    const amountPerTraveler = Math.abs(
      priceQuoteTotalPerTraveler - predictedTotalPerTraveler
    ).toFixed();
    const currency =
      priceQuote.itinerary.sellingPricing.totalPricing.total.fiat
        .currencySymbol;

    return {
      // If the difference is within a dollar we don't show the error modal
      hasDifference: difference >= 1,
      isIncrease: priceQuoteTotal > predictedTotal,
      predictedTotal,
      priceQuoteTotal,
      amount,
      amountPerTraveler,
      currency,
    };
  }
);

export const getRewardsConversionFailed = (state: IStoreState) =>
  state.flightBook.rewardsConversionFailed;

const getHasFlightBookError = createSelector(
  getUserPassengerCallState,
  getTripPricingCallState,
  getPassengersValidCallState,
  getFetchPriceDropEligibilityCallState,
  getFlightBookType,
  (
    callState,
    tripPricingError,
    passengerValidError,
    fetchPriceDropEligibilityCallState,
    flightBookType
  ): boolean => {
    let flightBookHasError: boolean;
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        flightBookHasError =
          callState === CallState.Failed ||
          tripPricingError === CallState.Failed ||
          passengerValidError === CallState.Failed ||
          fetchPriceDropEligibilityCallState === CallState.Failed;
        break;
      default:
        flightBookHasError = false;
        break;
    }
    return flightBookHasError;
  }
);

export const getHasError = createSelector(
  getIsWaitingPriceQuote,
  getPollPriceQuoteCallState,
  getSchedulePaymentError,
  getSchedulePriceQuoteError,
  getPriceDifferenceAcknowledged,
  getPriceDifference,
  getFinalizedCallState,
  getVerifyPaymentMethodCallState,
  getHasFlightBookError,
  getSetFlightBookQueryParamsCallState,
  getInfantOnlyError,
  (
    isWaitingPriceQuote,
    priceQuoteError,
    schedulePaymentError,
    schedulePriceQuoteError,
    acknowledged,
    priceDifference,
    finalizedError,
    verifyPaymentError,
    hasFlightBookError,
    setFlightBookQueryParamsCallState,
    infantOnlyError
  ) =>
    !isWaitingPriceQuote &&
    ((priceDifference.hasDifference && !acknowledged) ||
      !!schedulePaymentError ||
      !!schedulePriceQuoteError ||
      verifyPaymentError === CallState.Failed ||
      finalizedError === CallState.Failed ||
      priceQuoteError === CallState.Failed ||
      setFlightBookQueryParamsCallState === CallState.Failed ||
      hasFlightBookError ||
      infantOnlyError)
);

export const getVerifyPaymentMethodResult = (state: IStoreState) =>
  state.flightBook.verifyPaymentMethodResult;

export const getIsMissingPassport = createSelector(
  getPriceQuoteErrors,
  (errors) =>
    errors.length > 0 &&
    !!errors.find(
      (e) =>
        e === FlightError.MissingPassport ||
        e === FlightError.MissingPassportNumber
    )
);

export const getIsInvalidPassport = createSelector(
  getPriceQuoteErrors,
  (errors) =>
    errors.length > 0 &&
    !!errors.find((e) => e === FlightError.InvalidPassportNumber)
);

export const getSchedulePaymentCallState = (state: IStoreState) =>
  state.flightBook.schedulePaymentCallState;

export const selectedAncillaryIdsSelector = createSelector(
  selectedCfarIdSelector,
  selectedDisruptionProtectionOfferSelector,
  getFlightBookType,
  (cfarId, disruptionProtectionOffer, flightBookType) => {
    const ancillaryIds: ProtectionId[] = [];

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        if (
          cfarId &&
          cfarId.policyId !== DO_NOT_APPLY_OPTION_KEY &&
          cfarId?.offerType !== CfarOfferType.Hts
        ) {
          ancillaryIds.push({
            ...cfarId,
            ProtectionPolicyId: ProtectionPolicyIdEnum.CfarId,
            ProtectionId: ProtectionIdEnum.ProtectionPolicyId,
          });
        }

        if (
          disruptionProtectionOffer &&
          disruptionProtectionOffer.id.policyId !== DO_NOT_APPLY_OPTION_KEY &&
          !(
            disruptionProtectionOffer?.offerType?.OfferType ===
            DisruptionOfferType.Hts
          )
        ) {
          ancillaryIds.push({
            ...disruptionProtectionOffer.id,
            ProtectionPolicyId:
              disruptionProtectionOffer.AncillaryOffer ===
                AncillaryOfferEnum.DelayOffer
                ? ProtectionPolicyIdEnum.DelayId
                : ProtectionPolicyIdEnum.MissedConnectionId,
            ProtectionId: ProtectionIdEnum.ProtectionPolicyId,
          });
        }
        break;
      default:
        break;
    }

    return ancillaryIds;
  }
);

export const selectedHtsAncillaryIdsSelector = createSelector(
  selectedDisruptionProtectionOfferSelector,
  selectedCfarIdSelector,
  getFlightBookType,
  (disruptionProtectionOffer, cfarId, flightBookType) => {
    const htsAncillaryIds: HtsAncillaryId[] = [];

    switch (flightBookType) {
      case FlightBookType.DEFAULT:
        if (
          disruptionProtectionOffer &&
          disruptionProtectionOffer.id.policyId !== DO_NOT_APPLY_OPTION_KEY &&
          disruptionProtectionOffer?.offerType?.OfferType ===
          DisruptionOfferType.Hts
        ) {
          htsAncillaryIds.push({
            offerId: disruptionProtectionOffer.id.quoteId!,
            HtsAncillaryId: HtsAncillaryIdType.HtsDisruptionId,
          });
        }
        if (
          cfarId &&
          cfarId.policyId !== DO_NOT_APPLY_OPTION_KEY &&
          cfarId?.offerType === CfarOfferType.Hts &&
          cfarId.quoteId
        ) {
          htsAncillaryIds.push({
            offerId: cfarId.quoteId,
            HtsAncillaryId: HtsAncillaryIdType.HtsCfarId,
          });
        }
        break;
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      default:
        break;
    }
    return htsAncillaryIds;
  }
);

export const hasActiveRefundableFareInFlightBookSelector = createSelector(
  selectedCfarIdSelector,
  hasSelectedRefundableFareSelector,
  isRefundableFaresEnabledSelector,
  getFlightBookType,
  (
    selectedCfarId,
    hasSelectedRefundableFare,
    isRefundableFaresEnabled,
    flightBookType
  ): boolean =>
    !!selectedCfarId &&
    selectedCfarId.policyId !== DO_NOT_APPLY_OPTION_KEY &&
    hasSelectedRefundableFare &&
    isRefundableFaresEnabled &&
    flightBookType === FlightBookType.DEFAULT
);

export const pricingParamsSelector = createSelector(
  selectedTripSelector,
  getUserPassengers,
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  selectedAncillaryIdsSelector,
  selectedChfarIdSelector,
  selectedCfarIdSelector,
  selectedDisruptionProtectionIdSelector,
  selectedDisruptionProtectionOfferSelector,
  (
    selectedTrip,
    userPassengers,
    selectedTravelerIds,
    selectedLapInfants,
    selectedAncillaryIds,
    selectedChfarId,
    selectedCfarId,
    selectedDafarId,
    selectedDisruptionProtectionOffer
  ): PassengersForFareRequest => {
    const selectedOWRTrip = selectedTrip as ISelectedTrip;
    const selectedMcTrip = selectedTrip as ISelectedMulticityTrip;
    const fareId =
      selectedOWRTrip.returnFareId ||
      selectedOWRTrip.outgoingFareId ||
      selectedMcTrip.departureFareId ||
      "";

    // we need to convert this to ProtectionId to fetch it correctly from Tysons cache
    let updatedAncillaryIds: ProtectionId[] =
      selectedCfarId?.offerType === CfarOfferType.Hts
        ? [
          {
            productId: selectedCfarId?.productId,
            policyId: selectedCfarId?.policyId,
            quoteId: selectedCfarId?.quoteId,
            ProtectionPolicyId: ProtectionPolicyIdEnum.CfarId,
            ProtectionId: ProtectionIdEnum.ProtectionPolicyId,
          } as ProtectionId,
        ]
        : selectedAncillaryIds;

    if (
      selectedDisruptionProtectionOffer?.offerType?.OfferType ===
      DisruptionOfferType.Hts
    ) {
      const htsDisruptionProtectionId: ProtectionId = {
        productId: selectedDafarId?.productId,
        policyId: "",
        quoteId: selectedDafarId?.quoteId,
        ProtectionPolicyId: ProtectionPolicyIdEnum.DelayId,
        ProtectionId: ProtectionIdEnum.ProtectionPolicyId,
      } as ProtectionId;
      updatedAncillaryIds = [...updatedAncillaryIds, htsDisruptionProtectionId];
    }
    return {
      tripId: selectedTrip.tripId || "",
      seatedPassengers: userPassengers
        .filter((p) => selectedTravelerIds.includes(p.id))
        .map((u) => u.id),
      lapInfants: userPassengers
        .filter((p) => selectedLapInfants.includes(p.id))
        .map((u) => u.id),
      fareId: fareId!,
      ancillaryIds: updatedAncillaryIds,
      chfarQuoteId:
        selectedChfarId == null ||
          selectedChfarId.quoteId == DO_NOT_APPLY_OPTION_KEY
          ? undefined
          : selectedChfarId.quoteId,
    };
  }
);

export const getContactEmail = (state: IStoreState) =>
  state.flightBook.confirmationEmailAddress;

export const getContactPhone = (state: IStoreState) =>
  state.flightBook.confirmationPhoneNumber;

export const currentPriceDropProtectionSelector = createSelector(
  getFlightBookPriceDropProtection,
  getFlightBookType,
  (priceDropProtection, flightBookType): PriceDropProtection | null => {
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        return priceDropProtection;
      default:
        return null;
    }
  }
);

export const isFareOptimizationCreditShopTimeSelector = createSelector(
  currentPriceDropProtectionSelector,
  (priceDropProtection): boolean =>
    Boolean(
      priceDropProtection?.PriceDropProtection ===
      PriceDropProtectionEnum.IsEligible &&
      (priceDropProtection as unknown as ShoppingIsEligible)
        .isFareOptimizationCredit
    )
);

export const isPriceDropFOCreditBannerEligibleSelector = createSelector(
  isFareOptimizationCreditShopTimeSelector,
  getPriceQuoteWithUpdatedAncillary,
  (isFareOptimizationCreditShopTime, priceQuote): boolean => {
    const isFareOptimizationCreditEligibleQuoteTime = (
      priceQuote as PriceQuoteData
    )?.fareOptimizationCreditEligible;

    return Boolean(
      isFareOptimizationCreditShopTime &&
      isFareOptimizationCreditEligibleQuoteTime
    );
  }
);

export const priceQuoteDisruptionProtectionRebookParamsSelector =
  createSelector(
    singleFlightItineraryPassengersSelector,
    singleFlightItineraryIdSelector,
    (passengers, itineraryId) => ({
      passengers,
      itineraryId,
    })
  );

export const priceQuoteParamsSelector = createSelector(
  selectedTripSelector,
  getUserPassengers,
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  getContactEmail,
  getContactPhone,
  getAgentEmail,
  currentPriceDropProtectionSelector,
  selectedAncillaryIdsSelector,
  priceQuoteDisruptionProtectionRebookParamsSelector,
  getFlightBookType,
  selectedChfarIdSelector,
  selectedHtsAncillaryIdsSelector,
  (
    selectedTrip,
    userPassengers,
    selectedTravelerIds,
    selectedLapInfantIds,
    email,
    phone,
    agentEmail,
    priceDropProtection,
    ancillaryIds,
    disruptionProtectionRebookParams,
    flightBookType,
    selectedChfarId,
    htsAncillaryIds
  ): SchedulePriceQuoteRequest => {
    const { passengers: originalPassengers, itineraryId: originalItineraryId } =
      disruptionProtectionRebookParams;
    const getTripId = () => {
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          return selectedTrip.tripId ?? "";
      }
    };
    const getFareId = () => {
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          const selectedOWRTrip = selectedTrip as ISelectedTrip;
          const selectedMcTrip = selectedTrip as ISelectedMulticityTrip;
          return (
            selectedOWRTrip.returnFareId ||
            selectedOWRTrip.outgoingFareId ||
            selectedMcTrip.departureFareId ||
            ""
          );
      }
    };
    const getSeatedPassengers = () => {
      switch (flightBookType) {
        case FlightBookType.DISRUPTION_PROTECTION_EXERCISE:
          const originalPassengersSet = new Set<string>();
          originalPassengers?.alone.forEach((passenger) =>
            originalPassengersSet.add(passenger.person.id)
          );
          originalPassengers?.withLapInfants.forEach((entry) =>
            originalPassengersSet.add(entry.adult.person.id)
          );
          return Array.from(originalPassengersSet.values());
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          return userPassengers
            .filter((p) => selectedTravelerIds.includes(p.id))
            .map((u) => u.id);
      }
    };
    const getLapInfants = () => {
      switch (flightBookType) {
        case FlightBookType.DISRUPTION_PROTECTION_EXERCISE:
          return (
            originalPassengers?.withLapInfants.map(
              (infant) => infant.infant.person.id
            ) ?? []
          );
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          return userPassengers
            .filter((p) => selectedLapInfantIds.includes(p.id))
            .map((u) => u.id);
      }
    };
    const getEmailAddress = () => {
      switch (flightBookType) {
        case FlightBookType.DISRUPTION_PROTECTION_EXERCISE:
          return originalPassengers?.contact.contact.emailAddress ?? "";
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          return email ?? "";
      }
    };
    const getPhoneNumber = () => {
      switch (flightBookType) {
        case FlightBookType.DISRUPTION_PROTECTION_EXERCISE:
          return originalPassengers?.contact.contact.phoneNumber ?? "";
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          return phone ?? "";
      }
    };
    const getDisruptionProtection = () => {
      switch (flightBookType) {
        case FlightBookType.DISRUPTION_PROTECTION_EXERCISE:
          return {
            originalItineraryId: originalItineraryId ?? "",
          };
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default:
          return undefined;
      }
    };

    const candidateId = (priceDropProtection as IsEligible)?.candidateId;
    const params: SchedulePriceQuoteRequest = {
      tripId: getTripId(),
      seatedPassengers: getSeatedPassengers(),
      lapInfants: getLapInfants(),
      fareId: getFareId(),
      emailAddress: getEmailAddress(),
      phoneNumber: getPhoneNumber(),
      disruptionProtection: getDisruptionProtection(),
      ancillaryIds,
      htsAncillaryIds,
      chfarQuoteId:
        selectedChfarId == null ||
          selectedChfarId.quoteId == DO_NOT_APPLY_OPTION_KEY
          ? undefined
          : selectedChfarId.quoteId,
    };
    if (candidateId) {
      params.priceDropCandidateId = candidateId;
    }
    if (agentEmail) {
      params.delegatedTo = agentEmail;
    }
    return params;
  }
);

export const bookingInProgressSelector = createSelector(
  getSchedulePaymentCallState,
  getFinalizedCallState,
  getIsWaitingPriceQuote,
  isRefetchingPriceQuoteSelector,
  (
    paymentCallState,
    finalizedCallState,
    isWaitingPriceQuote,
    isRefetchingPriceQuote
  ) => {
    /*
      note: in the flight-book variant of https://app.launchdarkly.com/capital-one/test/features/c1-fintech-ancillary-marketplace/targeting, price quote and payment callstates can be in-progress simultaneously;
      when it's fetching a price quote, it needs to be non-refetching to be considered booking in progress
    */
    return (
      paymentCallState === CallState.InProcess ||
      finalizedCallState === CallState.InProcess ||
      (isWaitingPriceQuote && !isRefetchingPriceQuote)
    );
  }
);

export const hasNoUserPassengersSelector = createSelector(
  getUserPassengers,
  getUserPassengerCallState,
  (userPassengers, userPassengerCallState) =>
    userPassengers.length === 0 && userPassengerCallState === CallState.Success
);

// PAYMENT SELECTORS

export const getRewardsPaymentAccountReferenceId = (state: IStoreState) =>
  state.flightBook.rewardsAccountReferenceId;

export const getRewardsPaymentAccount = createSelector(
  getRewardsPaymentAccountReferenceId,
  getRewardsAccounts,
  (accountId, rewardsAccounts) =>
    rewardsAccounts.find((account) => account.accountReferenceId === accountId)
);

export const getRewardsPaymentAccountIfRedemptionEnabled = createSelector(
  getRewardsPaymentAccount,
  getEligibleRewardsAccountWithLargestValue,
  (rewardsAccount, largestRewardsAccount) =>
    rewardsAccount?.allowRewardsRedemption ?? true
      ? rewardsAccount || largestRewardsAccount
      : undefined
);

export const getSelectedRewardsAccounts = (state: IStoreState) =>
  state.flightBook.selectedRewardsAccounts;

export const getSelectedRewardsAccountReferenceIds = createSelector(
  getSelectedRewardsAccounts,
  (selectedRewardsAccounts) =>
    selectedRewardsAccounts.map((account) => account.accountReferenceId)
);

export const getIsMultiRewardsMode = createSelector(
  getSelectedRewardsAccounts,
  (selectedRewardsAccounts) => selectedRewardsAccounts.length > 0
);

export const getRewardsPaymentAccountReferenceIdIfRedemptionEnabled =
  createSelector(
    getRewardsPaymentAccountIfRedemptionEnabled,
    (rewardsAccount) => rewardsAccount?.accountReferenceId
  );

export const getUserSelectedPaymentMethod = createSelector(
  getPaymentMethods,
  getSelectedPaymentMethodId,
  (paymentMethods, selectedPaymentMethodId): CreditCard | undefined => {
    if (paymentMethods.length === 0 || !selectedPaymentMethodId) {
      return undefined;
    }

    return paymentMethods.find(
      (paymentMethod) => paymentMethod.id === selectedPaymentMethodId
    );
  }
);

export const getPassengerSeatMap = createSelector(
  getSelectedSeats,
  (seats): Map<string, number> => {
    const map = new Map();
    seats.forEach((seat) => {
      const total = map.get(seat.person_id);
      const seatPrice =
        seat.price_object.usd_total / 10 ** seat.price_object.decimal_places;
      if (total) {
        map.set(seat.person_id, total + seatPrice);
      } else {
        map.set(seat.person_id, seatPrice);
      }
    });
    return map;
  }
);

export const getSeatsTotalPricing = createSelector(
  getPassengerSeatMap,
  (passengerSeatMap) =>
    Array.from(passengerSeatMap.values()).reduce(
      (currentTotal, seatTotal) => currentTotal + seatTotal,
      0
    )
);

export const getSeatsRewardsPricing = createSelector(
  getSelectedSeats,
  (seats): Array<{ [key: string]: RewardsPrice }> => seats.map((s) => s.rewards)
);

export const getSeatsTotalRewardsPricing = createSelector(
  getRewardsPaymentAccount,
  getRewardsAccountWithLargestValue,
  getSeatsRewardsPricing,
  (
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    seatsRewardsPricing
  ) => {
    const activeRewardsAccount =
      rewardsPaymentAccount ?? rewardsAccountWithLargestValue;

    if (!activeRewardsAccount) return null;

    const { accountReferenceId } = activeRewardsAccount;

    if (seatsRewardsPricing.length > 0) {
      const seatsRewardsTotal = seatsRewardsPricing.reduce(
        (total: number, rewardsMap: { [key: string]: RewardsPrice }) => {
          const rewardsPrice = rewardsMap[accountReferenceId];
          if (rewardsPrice) {
            return total + rewardsPrice.value;
          }
          return total;
        },
        0
      );
      const tripSeatsTotal: RewardsPrice = {
        value: seatsRewardsTotal,
        currency: seatsRewardsPricing[0][accountReferenceId].currency,
      };
      return tripSeatsTotal;
    }
    return null;
  }
);

export const getFlightTotal = createSelector(
  getTripPricing,
  getPriceQuoteWithUpdatedAncillary,
  getSeatsTotalPricing,
  (tripPricing, priceQuote, seatsTotalPricing) =>
    priceQuote && priceQuote?.itinerary.sellingPricing.totalPricing.total
      ? priceQuote?.itinerary.sellingPricing.totalPricing.total.fiat.value +
      seatsTotalPricing
      : tripPricing?.totalPricing?.total.fiat.value
);

export const getPricing = createSelector(
  getTripPricing,
  getPriceQuoteWithUpdatedAncillary,
  (tripPricing, priceQuote) =>
    priceQuote?.itinerary.sellingPricing || tripPricing
);

export const getPaymentMethodRewardsAccountId = (state: IStoreState) =>
  state.flightBook.paymentMethodRewardsAccountId;

export const getCardPaymentRewardsAccount = createSelector(
  getPaymentMethodRewardsAccountId,
  getRewardsAccounts,
  (accountId, rewardsAccounts) =>
    rewardsAccounts.find((account) => account.accountReferenceId === accountId)
);

export const getFlightTotalInRewards = createSelector(
  getRewardsPaymentAccountIfRedemptionEnabled,
  getRewardsAccountWithLargestValue,
  getPricing,
  getSeatsRewardsPricing,
  getCardPaymentRewardsAccount,
  (
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    tripPricing,
    seatsRewardsPricing,
    cardPaymentRewardsAccount
  ): RewardsPrice | null => {
    const activeRewardsAccount =
      rewardsPaymentAccount ||
      cardPaymentRewardsAccount ||
      rewardsAccountWithLargestValue;

    if (
      !tripPricing ||
      !activeRewardsAccount ||
      !(activeRewardsAccount.allowRewardsRedemption ?? true)
    )
      return null;

    const { accountReferenceId } = activeRewardsAccount;
    const tripTotal =
      tripPricing?.totalPricing.total.accountSpecific[accountReferenceId];

    if (seatsRewardsPricing.length > 0) {
      const seatsRewardsTotal = seatsRewardsPricing.reduce(
        (total: number, rewardsMap: { [key: string]: RewardsPrice }) => {
          const rewardsPrice = rewardsMap[accountReferenceId];
          return total + rewardsPrice.value;
        },
        0
      );
      const tripSeatsTotal: RewardsPrice = {
        value: seatsRewardsTotal + tripTotal.value,
        currency: tripTotal.currency,
      };
      return tripSeatsTotal;
    }
    return tripTotal;
  }
);

export const finalizedItineraryIdSelector = createSelector(
  getFinalizedItinerary,
  (finalizedItinerary) => finalizedItinerary?.id
);

export const finalizedRebookItineraryFlightSummaryDataSelector = createSelector(
  getFinalizedItinerary,
  contextFromRebookSummarySelector,
  (finalizedItinerary, context): IFlightSummaryData | undefined => {
    if (finalizedItinerary) {
      /*
        note: the BookedFlightItinerary type is duplicated in payment-machine.ts and itinerary.ts,
        and it's quite a pain to completely replace one of them in Redmond, because they each have so many dependencies
      */
      const { travelItinerary } = finalizedItinerary as unknown as BookedFlight;
      const { airlines: airlineMap = {}, airports: airportMap = {} } =
        context ?? {};

      // note: the rebooked itinerary is always a new itinerary with just a single slice
      // as we only allow rebooking one slice of the flight.
      const travelItinerarySlice = (() => {
        switch (travelItinerary.TravelItinerary) {
          case TravelItineraryEnum.SingleTravelItinerary:
            return travelItinerary.slices[0];
          case TravelItineraryEnum.MultiTravelItinerary:
            return travelItinerary.travelItineraries[0]?.slices[0];
          default:
            return undefined;
        }
      })();
      const segments = travelItinerarySlice?.segments ?? [];
      const fareShelf = travelItinerarySlice?.fareShelf;
      const tripSegments = getTripSegmentsFromItinerarySegments({
        segments,
        airportMap,
        airlineMap,
        useCityName: true,
      });
      const firstSegment = tripSegments[0];
      const lastSegment = tripSegments[tripSegments.length - 1];

      return {
        fareShelfBrandName: fareShelf?.brandName,
        fareShelfRating: fareShelf?.rating,
        fareShelfShortBrandName: fareShelf?.shortBrandName,
        tripSlice: {
          segments: tripSegments,
          cabinClassNames: segments.map((segment) => segment.cabinClassName),
          airlineCode: firstSegment?.airlineCode,
          airlineName: firstSegment?.airlineName,
          departureTime: firstSegment?.departureTime,
          arrivalTime: lastSegment?.arrivalTime,
          originName: `${firstSegment.originName} (${firstSegment.originCode})`,
          originCode: firstSegment?.originCode,
          destinationName: `${lastSegment?.destinationName} (${lastSegment?.destinationCode})`,
          destinationCode: lastSegment?.destinationCode,
          plusDays: getPlusDaysFromItinerarySegments(segments),
          stops: getStopsFromSegments(segments),
        },
      } as IFlightSummaryData;
    }

    return undefined;
  }
);

export const rebookedItineraryDpExerciseItineraryFactsPropertiesSelector =
  createSelector(
    getFinalizedItinerary,
    selectedTripDetailsSelector,
    singleFlightItinerarySelector,
    singleFlightItineraryReturnSliceSelector,
    isSelectingReturnFlightSelector,
    getPriceQuoteWithUpdatedAncillary,
    (
      finalizedItinerary,
      selectedTripDetails,
      originalItinerary,
      originalReturnSlice,
      isSelectingReturnFlight,
      priceQuote
    ): PartialRebookedItineraryDpExerciseItineraryFacts | undefined => {
      if (
        !!originalItinerary &&
        (!!finalizedItinerary || !!priceQuote || !!selectedTripDetails)
      ) {
        // finalized payment data
        const finalizedTravelItinerary = finalizedItinerary?.travelItinerary;
        const finalizedDepartureSlice = getSlicesFromTravelItinerary(
          finalizedTravelItinerary as any
        )?.[0];
        const finalizedFirstDepartureSegment =
          finalizedDepartureSlice?.segments[0];
        const finalizedLastDepartureSegment =
          finalizedDepartureSlice?.segments[
          finalizedDepartureSlice.segments.length - 1
          ];
        const finalizedTotalPrice =
          finalizedItinerary?.sellingPricing.totalPricing.total.fiat.value;
        // rebookSummary data
        const originalPricingByPassenger =
          originalItinerary.itinerary.bookedItinerary.sellingPricing
            .pricingByPassenger[0];
        const originalTotalPricing =
          originalItinerary.itinerary.bookedItinerary.sellingPricing
            .totalPricing;
        const totalPricing = priceQuote?.itinerary.sellingPricing.totalPricing;

        return {
          price_per_pax_usd:
            // note: pricingByPassenger.total does not seem to include ancillary prices - not sure if that's intended
            roundToTwoDecimals(
              originalPricingByPassenger.subtotal.fiat.value +
              originalPricingByPassenger.ancillaries.reduce(
                (sum, ancillary) => sum + ancillary.premium.fiat.value,
                0
              )
            ),
          rebooking_direction: isSelectingReturnFlight ? "return" : "outbound",
          rebooking_flow: "disruption",
          rebooking_tool: true,
          subtotal_usd: originalTotalPricing.subtotal.fiat.value,
          total_price: originalTotalPricing.total.fiat.value,
          total_price_usd: originalTotalPricing.total.fiat.value,
          trip_type: originalReturnSlice ? "round_trip" : "one_way",
          disruption_rebook_costs:
            finalizedTotalPrice ?? totalPricing?.total.fiat.value,
          rebook_origin: finalizedFirstDepartureSegment?.origin.locationCode,
          rebook_destination:
            finalizedLastDepartureSegment?.destination.locationCode,
        };
      }

      return undefined;
    }
  );

export const rebookedItineraryDpDisruptionPolicyExercisedFactsPropertiesSelector =
  createSelector(
    getFinalizedItinerary,
    singleFlightItinerarySelector,
    dpExercisePassengerPropertiesSelector,
    (
      finalizedItinerary,
      originalItinerary,
      passengerProperties
    ): PartialRebookedItineraryDpDisruptionPolicyExercisedFacts &
      PartialPassengerDpExerciseFlightsListFacts => {
      // finalized payment data
      const finalizedPassengers = finalizedItinerary?.passengers;
      const finalizedPassengersCount = (() => {
        if (!finalizedPassengers) {
          return undefined;
        }

        const originalPassengersSet = new Set<string>();
        finalizedPassengers.alone.forEach((passenger) =>
          originalPassengersSet.add(passenger.person.id)
        );
        finalizedPassengers.withLapInfants.forEach((entry) => {
          originalPassengersSet.add(entry.adult.person.id);
          originalPassengersSet.add(entry.infant.person.id);
        });
        return originalPassengersSet.size;
      })();
      const finalizedLocators = finalizedItinerary?.travelItinerary.locators;
      // rebookSummary data
      const originalTotalPricing =
        originalItinerary?.itinerary.bookedItinerary.sellingPricing
          .totalPricing;
      const originalPricingByPassenger =
        originalItinerary?.itinerary.bookedItinerary.sellingPricing
          .pricingByPassenger[0];
      const originalLocators =
        originalItinerary?.itinerary.bookedItinerary.travelItinerary.locators;
      const ancillaries = originalItinerary?.itinerary.ancillaries;
      const delay = ancillaries?.delay;
      const missedConnection = ancillaries?.missedConnection;

      return {
        pax_total:
          finalizedPassengersCount ?? passengerProperties?.searched_pax_total,
        ...passengerProperties,
        exercise_choice: "rebook",
        base_total_usd: originalTotalPricing?.baseWithoutMargin.fiat.value,
        disruption_new_system_locator: finalizedLocators?.b2b,
        disruption_original_agent_locator:
          originalLocators?.agent.unscopedValue,
        disruption_original_system_locator: originalLocators?.b2b,
        disruption_original_trip_cost:
          originalPricingByPassenger?.subtotal.fiat.value,
        disruption_policy_id:
          delay?.id.policyId ?? missedConnection?.id.policyId,
        disruption_premium: ancillaries
          ? getCoverageAndPremiumFromDisruptionAncillaries(ancillaries)?.premium
            .amount
          : undefined,
        disruption_product_id:
          delay?.id.productId ?? missedConnection?.id.productId,
      };
    }
  );

export const rebookedItineraryDpRebookingCompleteBuyFactsPropertiesSelector =
  createSelector(
    getFinalizedItinerary,
    isSelectingReturnFlightSelector,
    selectedTripDetailsSelector,
    (
      finalizedItinerary,
      isSelectingReturnFlight,
      selectedTripDetails
    ): PartialRebookedItineraryDpRebookingCompleteBuyFacts => {
      // finalized payment data
      const finalizedTravelItinerary = finalizedItinerary?.travelItinerary;
      const finalizedDepartureSlice = getSlicesFromTravelItinerary(
        finalizedTravelItinerary as any
      )?.[0];
      const finalizedFirstDepartureSegment =
        finalizedDepartureSlice?.segments[0];
      const finalizedLastDepartureSegment =
        finalizedDepartureSlice?.segments[
        finalizedDepartureSlice.segments.length - 1
        ];
      const finalizedDepartureDate =
        finalizedFirstDepartureSegment?.zonedUpdatedDeparture ??
        finalizedFirstDepartureSegment?.zonedScheduledDeparture ??
        finalizedFirstDepartureSegment?.updatedDeparture ??
        finalizedFirstDepartureSegment?.scheduledDeparture;
      const finalizedCarrier =
        finalizedFirstDepartureSegment?.marketingAirline.code;
      const finalizedOrigin =
        finalizedFirstDepartureSegment?.origin.locationCode;
      const finalizedDestination =
        finalizedLastDepartureSegment?.destination.locationCode;
      const finalizedSegmentsLength = finalizedDepartureSlice?.segments.length;
      // rebookSummary data
      // We only allow rebooking a single slice of a flight, so we can assume slices only contains one slice.
      const departureSlice = selectedTripDetails?.slices[0];
      const departureDate =
        departureSlice?.zonedDepartureTime ?? departureSlice?.departureTime;
      const carrier = departureSlice?.segmentDetails[0]?.marketingAirline.code;
      const origin = departureSlice?.originCode;
      const destination = departureSlice?.destinationCode;
      // active
      const segments =
        finalizedSegmentsLength ?? departureSlice?.segmentDetails.length;
      const stops = finalizedSegmentsLength
        ? finalizedSegmentsLength - 1
        : departureSlice?.stops;

      return {
        advance: dayjs(finalizedDepartureDate ?? departureDate).diff(
          dayjs(),
          "day"
        ),
        carrier: finalizedCarrier ?? carrier,
        rebook_origin: finalizedOrigin ?? origin,
        rebook_destination: finalizedDestination ?? destination,
        ...(isSelectingReturnFlight
          ? {
            return_segments: segments,
            return_stops: stops,
          }
          : {
            outbound_segments: segments,
            outbound_stops: stops,
          }),
      };
    }
  );

export const getRewardsPaymentInFiatCurrency = (state: IStoreState) =>
  state.flightBook.rewardsPaymentInFiatCurrency;

export const getRewardsPaymentInRewardsCurrency = (state: IStoreState) =>
  state.flightBook.rewardsPaymentTotal;

export const getRewardsRemainingAfterPurchase = createSelector(
  getRewardsPaymentAccount,
  getRewardsPaymentInFiatCurrency,
  (paymentAccount, rewardsPaymentAmount) => {
    if (!paymentAccount) return "";

    const {
      rewardsCashEquivalent: { value, currencyCode, currencySymbol },
    } = paymentAccount;

    const remainder = value - (rewardsPaymentAmount?.value || 0);

    return getTotalPriceText({
      price: {
        currencyCode,
        currencySymbol,
        value: remainder,
      },
      priceFormatter: twoDecimalFormatter,
    });
  }
);

export const getChfarExerciseDiscountAmount = (state: IStoreState) =>
  state.flightBook.chfarExerciseDiscountAmount;

export const getChfarExerciseTrackingProperties = (state: IStoreState) =>
  state.flightBook.chfarExerciseTrackingProperties;

export const getChfarExerciseDiscountAmountFiat = createSelector(
  [getChfarExerciseDiscountAmount],
  (chfarExerciseDiscountAmount) => chfarExerciseDiscountAmount?.fiat
);

export const getPaymentTotalInPrices = createSelector(
  getPriceQuoteWithUpdatedAncillary,
  getTripPricing,
  getFlightBookType,
  getSeatsTotalPricing,
  getSeatsRewardsPricing,
  getTravelWalletItemsToApply,
  getChfarExerciseDiscountAmount,
  (
    flightPriceQuote,
    tripPricing,
    flightBookType,
    seatsTotal,
    seatsRewardsPricing,
    travelWalletItemsToApply,
    chfarExerciseDiscountAmount
  ): Prices | undefined => {
    const chfarDiscountAmountFiat =
      chfarExerciseDiscountAmount?.fiat.value ?? 0;

    const chfarDiscountAmountRewards =
      chfarExerciseDiscountAmount?.accountSpecific ?? {};

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        let walletItemsTotal: number = 0;
        if (
          travelWalletItemsToApply.offerToApply ||
          travelWalletItemsToApply.creditToApply
        ) {
          walletItemsTotal =
            travelWalletItemsToApply.offerToApply?.amount.amount || 0;
          if (travelWalletItemsToApply.creditToApply) {
            const maxApplicableCredit =
              travelWalletItemsToApply.offerToApply?.maxApplicableCredit
                ?.amount;
            if (
              maxApplicableCredit &&
              maxApplicableCredit >
              travelWalletItemsToApply.creditToApply.amount.amount
            ) {
              walletItemsTotal += maxApplicableCredit;
            } else {
              walletItemsTotal +=
                travelWalletItemsToApply.creditToApply.amount.amount;
            }
          }
        }
        if (flightPriceQuote) {
          const tripTotal =
            flightPriceQuote.itinerary.sellingPricing.totalPricing.total;
          const rewardsPricingMap = {};

          Object.keys(tripTotal.accountSpecific).forEach((accountId) => {
            const seatTotal = seatsRewardsPricing.reduce(
              (total, seatRewardsPricing) =>
                total + seatRewardsPricing[accountId].value,
              0
            );

            rewardsPricingMap[accountId] = {
              ...tripTotal.accountSpecific[accountId],
              // There is some weird rounding issue with the rewards value
              value:
                seatTotal +
                tripTotal.accountSpecific[accountId].value -
                //  Subtracting the CHFAR discount reward amount
                (chfarDiscountAmountRewards[accountId]?.value || 0),
            };

            if (
              travelWalletItemsToApply.offerToApply ||
              travelWalletItemsToApply.creditToApply
            ) {
              let rewardTotalValue = rewardsPricingMap[accountId].value;
              const rewardTotalWithWalletItem =
                tripTotal.accountSpecific[accountId].currency === "Cash"
                  ? rewardTotalValue + walletItemsTotal
                  : rewardTotalValue + walletItemsTotal * 100;
              rewardTotalValue =
                rewardTotalWithWalletItem > 0 ? rewardTotalWithWalletItem : 0;
              rewardsPricingMap[accountId] = {
                ...tripTotal.accountSpecific[accountId],
                value: rewardTotalValue,
              };
            }
          });
          if (
            travelWalletItemsToApply.offerToApply ||
            travelWalletItemsToApply.creditToApply
          ) {
            return {
              fiat: {
                ...tripTotal.fiat,
                value: Math.max(
                  roundToTwoDecimals(
                    tripTotal.fiat.value +
                    seatsTotal +
                    walletItemsTotal -
                    //  Subtracting the CHFAR discount fiat amount
                    chfarDiscountAmountFiat
                  ),
                  0
                ),
              },
              accountSpecific: roundRewards(rewardsPricingMap),
            };
          }
          return {
            fiat: {
              ...tripTotal.fiat,
              value: roundToTwoDecimals(
                tripTotal.fiat.value + seatsTotal - chfarDiscountAmountFiat
              ),
            },
            accountSpecific: roundRewards(rewardsPricingMap),
          };
        }
        // For ChFAR, we expect there to be a quoted price, so we're not going to handle the cases below
        if (
          travelWalletItemsToApply.offerToApply ||
          travelWalletItemsToApply.creditToApply
        ) {
          const tripTotal = tripPricing?.totalPricing.total;
          const rewardsPricingMap = {};

          if (tripTotal) {
            Object.keys(tripTotal.accountSpecific).forEach((accountId) => {
              if (
                travelWalletItemsToApply.offerToApply ||
                travelWalletItemsToApply.creditToApply
              ) {
                let rewardTotalValue =
                  tripTotal.accountSpecific[accountId].value;
                rewardTotalValue =
                  tripTotal.accountSpecific[accountId].currency === "Cash"
                    ? rewardTotalValue + walletItemsTotal
                    : rewardTotalValue + walletItemsTotal * 100;
                rewardsPricingMap[accountId] = {
                  ...tripTotal.accountSpecific[accountId],
                  value: rewardTotalValue,
                };
              }
            });
            return {
              fiat: {
                ...tripTotal.fiat,
                value: Math.max(
                  roundToTwoDecimals(tripTotal.fiat.value + walletItemsTotal),
                  0
                ),
              },
              accountSpecific: roundRewards(rewardsPricingMap),
            };
          }
        }
        return tripPricing?.totalPricing.total;

      default:
        return undefined;
    }
  }
);

// TODO: this value should come from the backend
// note: it's needed when no priceQuote has been requested yet

export const getPricingInfoFlightTotalInPrices = createSelector(
  getRewardsPaymentAccount,
  getRewardsAccountWithLargestValue,
  shopPricingInfoSelector,
  getFlightBookType,
  (
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    pricingInfo,
    flightBookType
  ): { fiat: FiatPrice; rewards: RewardsPrice } | null => {
    const activeRewardsPaymentAccount =
      rewardsPaymentAccount ?? rewardsAccountWithLargestValue;

    if (!activeRewardsPaymentAccount) {
      return null;
    }
    const { accountReferenceId } = activeRewardsPaymentAccount;

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
      default:
        if (!pricingInfo.fare || pricingInfo.fare.length === 0) {
          return null;
        }
        const farePricing = pricingInfo.fare[0].pricing;

        const fiatBaseAmount = farePricing.baseAmount.fiat.value || 0;
        const fiatTaxAmount = farePricing.taxAmount.fiat.value || 0;
        const fiatAdditionalMargin = farePricing.additionalMargin
          ? farePricing.additionalMargin.fiat.value
          : 0;

        const rewardsBaseAmount =
          farePricing.baseAmount.rewards[accountReferenceId]?.value ?? 0;
        const rewardsTaxAmount =
          farePricing.taxAmount.rewards[accountReferenceId]?.value ?? 0;
        const rewardsAdditionalMargin = farePricing.additionalMargin
          ? farePricing.additionalMargin.rewards[accountReferenceId].value
          : 0;

        const fiatTotal = fiatBaseAmount + fiatTaxAmount + fiatAdditionalMargin;
        const rewardsTotal =
          rewardsBaseAmount + rewardsTaxAmount + rewardsAdditionalMargin;

        return {
          fiat: {
            currencyCode: farePricing.baseAmount.fiat.currencyCode,
            currencySymbol: farePricing.baseAmount.fiat.currencySymbol,
            value: fiatTotal,
          },
          rewards: {
            value: rewardsTotal,
            currency:
              activeRewardsPaymentAccount.rewardsBalance.currencyDescription ||
              activeRewardsPaymentAccount.rewardsBalance.currency,
          },
        };
    }
  }
);

export const currencySelector = createSelector(
  shopPricingInfoSelector,
  getFlightBookType,
  (
    pricingInfo,
    flightBookType
  ): {
    currencyCode: string;
    currencySymbol: string;
  } => {
    // TODO: as Mer has noted, we should remove hard coded currency values
    let currency = {
      currencyCode: Currency.USD,
      currencySymbol: "$",
    };

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        if (pricingInfo.fare && pricingInfo.fare.length > 0) {
          currency = {
            currencyCode: pricingInfo.fare[0].pricing.baseAmount.fiat
              .currencyCode as Currency,
            currencySymbol:
              pricingInfo.fare[0].pricing.baseAmount.fiat.currencySymbol,
          };
        }
        break;
      default:
        break;
    }

    return currency;
  }
);

export const getRewardsAllocations = createSelector(
  getSelectedRewardsAccounts,
  getRewardsAccounts,
  getPaymentTotalInPrices,
  currencySelector,
  (selectedRewardsAccounts, rewardsAccounts, total, currency) =>
    resolveRewardsAllocations(
      selectedRewardsAccounts.map((selectedAccount) => ({
        accountReferenceId: selectedAccount.accountReferenceId,
        customFiatValue:
          selectedAccount.rewardsPaymentInFiatCurrency?.value ?? undefined,
      })),
      rewardsAccounts,
      total,
      currency
    )
);

export const getRewardsPaymentInFiatCurrencyTotal = createSelector(
  getIsMultiRewardsMode,
  getRewardsAllocations,
  getRewardsPaymentInFiatCurrency,
  currencySelector,
  (
    isMultiRewardsMode,
    allocations,
    singleRewardsPaymentInFiatCurrency,
    currency
  ): FiatPrice | null => {
    if (!isMultiRewardsMode) {
      return singleRewardsPaymentInFiatCurrency;
    }
    const value = allocations.reduce(
      (sum, allocation) => sum + allocation.fiat.value,
      0
    );
    return { ...currency, value: roundToTwoDecimals(value) };
  }
);

export const getIsCreditCardPaymentRequired = createSelector(
  getFlightTotal,
  getRewardsPaymentInFiatCurrencyTotal,
  getFlightBookType,
  getPricingInfoFlightTotalInPrices,
  getPaymentTotalInPrices,
  getTravelWalletItemsToApply,
  (
    flightTotal,
    rewardsPaymentAmount,
    flightBookType,
    pricingInfoFlightTotalInPrices,
    paymentTotal,
    travelWalletItemsToApply
  ) => {
    let total: number | undefined;
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        if (flightTotal) {
          total = paymentTotal?.fiat.value;
        } else {
          // flightTotal is 0 until a pax is selected so we need pricing from pricingInfo selector
          total = pricingInfoFlightTotalInPrices?.fiat.value;
          if (
            (travelWalletItemsToApply.offerToApply ||
              travelWalletItemsToApply.creditToApply) &&
            pricingInfoFlightTotalInPrices
          ) {
            const offerToApplyAmount =
              travelWalletItemsToApply.offerToApply?.amount.amount || 0;
            const creditToApplyAmount =
              travelWalletItemsToApply.creditToApply?.amount.amount || 0;
            const totalWithWalletItem =
              pricingInfoFlightTotalInPrices.fiat.value +
              offerToApplyAmount +
              creditToApplyAmount;
            total = totalWithWalletItem > 0 ? totalWithWalletItem : 0;
          }
        }
        break;
      default:
        break;
    }

    if (!total) return true;
    if (!rewardsPaymentAmount) return true;

    return total > rewardsPaymentAmount.value;
  }
);

export const getIsMultiRewardsBalanceFullyCovered = createSelector(
  getIsMultiRewardsMode,
  getRewardsPaymentInFiatCurrencyTotal,
  getPaymentTotalInPrices,
  (isMultiRewardsMode, rewardsPaymentInFiatCurrencyTotal, total): boolean => {
    if (!isMultiRewardsMode || !total) {
      return false;
    }
    return (rewardsPaymentInFiatCurrencyTotal?.value ?? 0) >= total.fiat.value;
  }
);

export const getChfarExerciseDiscountAmountRewards = createSelector(
  [
    getChfarExerciseDiscountAmount,
    getRewardsPaymentAccountIfRedemptionEnabled,
    getRewardsAccountWithLargestValue,
    getCardPaymentRewardsAccount,
  ],
  (
    chfarExerciseDiscountAmount,
    rewardsPaymentAccount,
    rewardsAccountWithLargestValue,
    cardPaymentRewardsAccount
  ) => {
    const activeRewardsAccount =
      rewardsPaymentAccount ||
      cardPaymentRewardsAccount ||
      rewardsAccountWithLargestValue;

    if (
      !chfarExerciseDiscountAmount ||
      !activeRewardsAccount ||
      !(activeRewardsAccount.allowRewardsRedemption ?? true)
    )
      return null;

    const { accountReferenceId } = activeRewardsAccount;

    return chfarExerciseDiscountAmount.accountSpecific[accountReferenceId];
  }
);

export const getTotalCreditCardPaymentRequiredInFiatPrice = createSelector(
  getFlightTotal,
  getRewardsPaymentInFiatCurrencyTotal,
  currencySelector,
  getFlightBookType,
  getPricingInfoFlightTotalInPrices,
  getTravelWalletItemsToApply,
  getPaymentTotalInPrices,
  (
    flightTotal,
    rewardsPaymentAmount,
    currency,
    flightBookType,
    pricingInfoFlightTotalInPrices,
    travelWalletItemsToApply,
    paymentTotal
  ): FiatPrice => {
    let total: number | undefined;

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        if (flightTotal) {
          total = paymentTotal?.fiat.value;
        } else {
          // flightTotal is 0 until a pax is selected so we need pricing from pricingInfo selector
          total = pricingInfoFlightTotalInPrices?.fiat.value;
          if (
            (travelWalletItemsToApply.offerToApply ||
              travelWalletItemsToApply.creditToApply) &&
            pricingInfoFlightTotalInPrices
          ) {
            const offerToApplyAmount =
              travelWalletItemsToApply.offerToApply?.amount.amount || 0;
            const creditToApplyAmount =
              travelWalletItemsToApply.creditToApply?.amount.amount || 0;
            const totalWithWalletItem =
              pricingInfoFlightTotalInPrices?.fiat.value +
              offerToApplyAmount +
              creditToApplyAmount;
            total = totalWithWalletItem > 0 ? totalWithWalletItem : 0;
          }
        }

        break;
      default:
        break;
    }

    if (!total) {
      return {
        ...currency,
        value: 0,
      };
    }

    if (!rewardsPaymentAmount) {
      return {
        ...currency,
        value: roundToTwoDecimals(total),
      };
    }
    const remainder = total - rewardsPaymentAmount.value;
    const final = Math.max(roundToTwoDecimals(remainder), 0);
    return {
      ...currency,
      value: final,
    };
  }
);

export const getTotalCreditCardPaymentRequired = createSelector(
  getTotalCreditCardPaymentRequiredInFiatPrice,
  (totalCreditCardPaymentRequired): string => {
    if (totalCreditCardPaymentRequired.value === 0) return "";

    return getTotalPriceText({
      price: totalCreditCardPaymentRequired,
      priceFormatter: twoDecimalFormatter,
    });
  }
);

export const getTotalCreditCardPaymentRequiredNumber = createSelector(
  getTotalCreditCardPaymentRequiredInFiatPrice,
  (totalCreditCardPaymentRequired): number | null => {
    if (totalCreditCardPaymentRequired.value === 0) return null;

    return totalCreditCardPaymentRequired.value;
  }
);

export const getCreditsByPerson = createSelector(
  getPriceQuoteWithUpdatedAncillary,
  (priceQuote): PersonCredits[] | null =>
    priceQuote?.creditSummary?.credits.creditsByPerson ?? null
);

export interface ILineItemProps {
  lineTitle?: string;
  baseAmount?: number;
  taxesAndFees?: number;
  seats?: number;
  frozenPrice?: boolean;
  icon?: IconName;
  isTravelOffer?: boolean;
  label?: string;
  cfarFee?: number;
  chfarFee?: number;
  origCfarFee?: number;
  disruptionProtectionFee?: number;
}

export const getTotalBeforeWalletItems = createSelector(
  getTripPricing,
  getPriceQuoteWithUpdatedAncillary,
  getSeatsTotalPricing,
  getPricingInfoFlightTotalInPrices,
  getChfarExerciseDiscountAmountFiat,
  (
    tripPricing,
    priceQuote,
    seatsTotalPricing,
    pricingInfo,
    chfarExerciseDiscountAmountFiat
  ) => {
    // Here we get the price based on the available data, i.e., quote, tripPricing, or pricingInfo
    let totalBeforeWalletItems = 0;

    if (
      priceQuote?.itinerary?.sellingPricing?.totalPricing?.total?.fiat?.value !=
      null
    ) {
      totalBeforeWalletItems =
        priceQuote.itinerary.sellingPricing.totalPricing.total.fiat.value +
        seatsTotalPricing;
    } else if (tripPricing?.totalPricing?.total?.fiat?.value != null) {
      totalBeforeWalletItems = tripPricing.totalPricing.total.fiat.value;
    } else if (pricingInfo?.fiat?.value != null) {
      totalBeforeWalletItems = pricingInfo.fiat.value;
    }

    // Here we apply any non-wallet discounts, i.e., ChFAR
    if (chfarExerciseDiscountAmountFiat) {
      totalBeforeWalletItems = Math.max(
        0,
        totalBeforeWalletItems - chfarExerciseDiscountAmountFiat.value
      );
    }

    return totalBeforeWalletItems;
  }
);

export const getTravelWalletOfferToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  getTotalBeforeWalletItems,
  (travelWalletItemsToApply, totalBeforeWalletItems) => {
    if (travelWalletItemsToApply.offerToApply) {
      const totalAfterOffer =
        totalBeforeWalletItems +
        travelWalletItemsToApply.offerToApply.amount.amount;
      return totalAfterOffer < 0
        ? totalBeforeWalletItems
        : travelWalletItemsToApply.offerToApply.amount.amount * -1;
    }
    return 0;
  }
);

export const getTravelWalletCreditToApplyAmount = createSelector(
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTotalBeforeWalletItems,
  (travelWalletItemsToApply, offerAppliedAmount, totalBeforeWalletItems) => {
    const totalFiatValueWithOfferApplied: number =
      totalBeforeWalletItems - offerAppliedAmount;
    if (
      travelWalletItemsToApply.creditToApply &&
      totalFiatValueWithOfferApplied > 0
    ) {
      const maxApplicableCredit =
        travelWalletItemsToApply.offerToApply?.maxApplicableCredit?.amount;

      const amount =
        maxApplicableCredit &&
          maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount
          ? maxApplicableCredit
          : travelWalletItemsToApply.creditToApply.amount.amount;
      if (totalFiatValueWithOfferApplied < amount * -1) {
        if (
          maxApplicableCredit &&
          maxApplicableCredit <
          travelWalletItemsToApply.creditToApply.amount.amount &&
          travelWalletItemsToApply.creditToApply.amount.amount * -1 <
          totalFiatValueWithOfferApplied
        ) {
          return travelWalletItemsToApply.creditToApply.amount.amount * -1;
        }
        return totalFiatValueWithOfferApplied;
      }
      if (
        maxApplicableCredit &&
        maxApplicableCredit <
        travelWalletItemsToApply.creditToApply.amount.amount
      ) {
        return travelWalletItemsToApply.creditToApply.amount.amount * -1;
      }
      return amount * -1;
    }
    return 0;
  }
);

export const getMaxApplicableTravelWalletCreditOnlyAmount = createSelector(
  getApplicableCredit,
  getTotalBeforeWalletItems,
  (credit, totalBeforeWalletItems) => {
    if (credit && totalBeforeWalletItems) {
      const totalAfterOffer =
        totalBeforeWalletItems + (credit.amount.amount ?? 0);
      return totalAfterOffer < 0
        ? totalBeforeWalletItems * -1
        : credit.amount.amount;
    }
    return 0;
  }
);

export const getMaxApplicableTravelWalletCreditAmount = createSelector(
  getApplicableCredit,
  getTravelWalletOfferToApplyAmount,
  getTotalBeforeWalletItems,
  (credit, offerAppliedAmount, totalBeforeWalletItems) => {
    if (credit && totalBeforeWalletItems) {
      const totalAfterOffer =
        totalBeforeWalletItems +
        (credit?.amount.amount ?? 0) -
        offerAppliedAmount;
      return totalAfterOffer < 0
        ? (totalBeforeWalletItems - offerAppliedAmount) * -1
        : credit.amount.amount;
    }
    return 0;
  }
);

export const getCompleteBuyTravelOfferProperties = createSelector(
  getTravelOfferProperties,
  getOfferToApply,
  getTravelWalletOfferToApplyAmount,
  (
    travelOfferProperties,
    offerToApply,
    offerToApplyAmount
  ): ITrackingProperties => ({
    properties: {
      ...travelOfferProperties.properties,
      offer_used: !!offerToApply,
      offer_amount_redeemed_usd:
        !!offerToApply && !!offerToApplyAmount
          ? Math.abs(offerToApplyAmount)
          : 0,
    },
    encryptedProperties: [...travelOfferProperties.encryptedProperties],
  })
);

export const priceQuoteLineItemsSelector = createSelector(
  getPricing,
  getPassengerSeatMap,
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  getUserPassengers,
  selectedDiscountedCfarOfferPricesSelector,
  (
    tripPricing,
    seatMap,
    passengersIds,
    infantIds,
    passengersInfo,
    discountedCfar
  ): ILineItemProps[] => {
    let lineItems: ILineItemProps[] = [];
    const selectedPassengers = [...passengersIds, ...infantIds];
    if (tripPricing && tripPricing.pricingByPassenger.length > 0) {
      if (selectedPassengers.length > tripPricing.pricingByPassenger.length) {
        lineItems = selectedPassengers.map((passenger) => {
          const paxPricing = tripPricing.pricingByPassenger.find(
            (pricing) => pricing.person.id === passenger
          );
          if (paxPricing) {
            // TODO: the actual model has yet to be decided by the BE
            const cfarFee = getCfarFee(paxPricing.ancillaries);
            const chfarFee = paxPricing.chfar?.fiat.value;
            const disruptionProtectionFee = paxPricing.ancillaries?.find(
              (ancillary: any) =>
                ancillary?.kind == "Delay" ||
                ancillary?.kind == "MissedConnection"
            )?.premium?.fiat?.value;

            const lineItem: ILineItemProps = {
              lineTitle: `${paxPricing.person.givenName} ${paxPricing.person.surname}`,
              baseAmount:
                paxPricing.baseWithoutMargin.fiat.value +
                (paxPricing.additionalMargin?.fiat.value ?? 0),
              taxesAndFees: paxPricing.taxes.fiat.value,
              frozenPrice: false,
            };
            const seats = seatMap.get(paxPricing.person.id);
            if (seats) {
              lineItem.seats = seats;
            }
            if (cfarFee !== undefined) {
              lineItem.cfarFee = cfarFee;
            }
            if (chfarFee !== undefined) {
              lineItem.chfarFee = chfarFee;
            }
            if (discountedCfar !== undefined) {
              lineItem.origCfarFee =
                discountedCfar.originalPremiumAmount.fiat.value;
            }
            if (disruptionProtectionFee !== undefined) {
              lineItem.disruptionProtectionFee = disruptionProtectionFee;
            }

            return lineItem;
          }
          const paxInfo = passengersInfo.find((pax) => pax.id === passenger);
          const lineItem: ILineItemProps = {
            lineTitle: `${paxInfo?.givenName} ${paxInfo?.surname}`,
            baseAmount: undefined,
            taxesAndFees: undefined,
            frozenPrice: false,
          };
          return lineItem;
        });
      } else {
        lineItems = tripPricing.pricingByPassenger.map((pricing) => {
          const cfarFee = getCfarFee(pricing.ancillaries);
          const chfarFee = pricing.chfar?.fiat.value;

          const disruptionProtectionFee = getDisruptionProtectionFee(
            pricing.ancillaries
          );

          // use latest paxInfo which might contain updated names
          const paxInfo = passengersInfo.find(
            (pax) => pax.id === pricing.person.id
          );
          const lineItem: ILineItemProps = {
            lineTitle: `${paxInfo?.givenName ?? pricing.person.givenName} ${paxInfo?.surname ?? pricing.person.surname
              }`,
            baseAmount:
              pricing.baseWithoutMargin.fiat.value +
              (pricing.additionalMargin?.fiat.value ?? 0),
            taxesAndFees: pricing.taxes.fiat.value,
            frozenPrice: false,
          };
          const seats = seatMap.get(pricing.person.id);
          if (seats) {
            lineItem.seats = seats;
          }
          if (cfarFee !== undefined) {
            lineItem.cfarFee = cfarFee;
          }
          if (chfarFee !== undefined) {
            lineItem.chfarFee = chfarFee;
          }
          if (discountedCfar !== undefined) {
            lineItem.origCfarFee =
              discountedCfar.originalPremiumAmount.fiat.value;
          }
          if (disruptionProtectionFee !== undefined) {
            lineItem.disruptionProtectionFee = disruptionProtectionFee;
          }
          return lineItem;
        });
      }
    } else if (tripPricing && tripPricing.totalPricing.total.fiat.value > 0) {
      const baseAmount =
        tripPricing.totalPricing.baseWithoutMargin.fiat.value || 0;
      const taxesAndFees = tripPricing.totalPricing.taxes.fiat.value || 0;
      const cfarFee = getCfarFee(tripPricing.totalPricing.ancillaries);
      const chfarFee = tripPricing.totalPricing.chfar?.fiat.value || 0;
      const origCfarFee = discountedCfar?.originalPremiumAmount.fiat.value;
      const disruptionProtectionFee = getDisruptionProtectionFee(
        tripPricing.totalPricing.ancillaries
      );

      lineItems = [
        {
          lineTitle: "",
          baseAmount,
          taxesAndFees,
          cfarFee,
          chfarFee,
          disruptionProtectionFee,
          origCfarFee,
        } as ILineItemProps,
      ];
    }

    return lineItems;
  }
);

export const totalTripPricingAncillaries = createSelector(
  priceQuoteLineItemsSelector,
  (priceQuoteLineItems): SelectedAncillaries | undefined => {
    return priceQuoteLineItems?.reduce(
      (acc, lineItem) =>
      ({
        cfarFee: sumFees(acc.cfarFee, lineItem.cfarFee),
        origCfarFee: sumFees(acc.origCfarFee, lineItem.origCfarFee),
        chfarFee: sumFees(acc.chfarFee, lineItem.chfarFee),
        disruptionProtectionFee: sumFees(
          acc.disruptionProtectionFee,
          lineItem.disruptionProtectionFee
        ),
      } as SelectedAncillaries),
      {} as SelectedAncillaries
    );

    function sumFees(fee1?: number, fee2?: number): number | undefined {
      if (fee1 == undefined && fee2 == undefined) {
        return undefined;
      }

      return (fee1 ?? 0) + (fee2 ?? 0);
    }
  }
);

export const getFdaPrice = createSelector(
  totalTripPricingAncillaries,
  (ancillaries): number | undefined => ancillaries?.disruptionProtectionFee
);

export const getFdaProductVariant = createSelector(
  selectedDisruptionProtectionOfferSelector,
  (offer): string | undefined => {
    return offer?.productVariant?.ProductVariant;
  }
);

export const getFlightTotalInPrices = createSelector(
  getFlightTotal,
  currencySelector,
  getFlightTotalInRewards,
  getPricingInfoFlightTotalInPrices,
  (
    flightTotal,
    currency,
    flightTotalInRewards,
    pricingInfoFlightTotalInPrices
  ): { fiat: FiatPrice; rewards?: RewardsPrice } | null => {
    if (flightTotal) {
      return {
        fiat: {
          ...currency,
          value: flightTotal,
        },
        rewards: flightTotalInRewards ?? undefined,
      };
    }
    if (pricingInfoFlightTotalInPrices) {
      return pricingInfoFlightTotalInPrices;
    }
    return null;
  }
);

export const getFlightPriceWithChfarDiscountApplied = createSelector(
  [
    getChfarExerciseDiscountAmountFiat,
    getChfarExerciseDiscountAmountRewards,
    getFlightTotalInPrices,
  ],
  (
    chfarExerciseDiscountFiat,
    chfarExerciseDiscountRewards,
    flightTotalInPrices
  ) => {
    if (chfarExerciseDiscountFiat && flightTotalInPrices) {
      return {
        fiat: {
          ...flightTotalInPrices.fiat,
          value: Math.max(
            roundToTwoDecimals(
              flightTotalInPrices.fiat.value - chfarExerciseDiscountFiat.value
            ),
            0
          ),
        },
        ...roundRewards(
          flightTotalInPrices.rewards && chfarExerciseDiscountRewards
            ? {
              rewards: {
                ...flightTotalInPrices.rewards,
                value: Math.max(
                  0,
                  flightTotalInPrices.rewards.value -
                  chfarExerciseDiscountRewards.value
                ),
              },
            }
            : {}
        ),
      };
    }
    return flightTotalInPrices;
  }
);

export const getQuotedRewardsTotal = (state: IStoreState) =>
  state.flightBook.quotedRewardsTotal;

export const getPriceQuoteSuccessTime = (state: IStoreState) =>
  state.flightBook.priceQuoteSuccessTime;

export const priceQuoteSummaryLineItemsSelector = createSelector(
  currencySelector,
  getRewardsPaymentAccount,
  getFlightTotalInPrices,
  getRewardsAccountWithLargestValue,
  getRewardsPaymentAccountReferenceId,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentInRewardsCurrency,
  getUserSelectedPaymentMethod,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getQuotedRewardsTotal,
  getTravelWalletItemsToApply,
  getTravelWalletCreditToApplyAmount,
  getTravelWalletOfferToApplyAmount,
  getChfarExerciseDiscountAmountFiat,
  getIsMultiRewardsMode,
  getRewardsAllocations,
  getRewardsAccounts,
  (
    currency,
    rewardsPaymentAccount,
    flightTotalInPrices,
    rewardsAccountWithLargestValue,
    rewardsPaymentAccountReferenceId,
    rewardsPaymentInFiatCurrency,
    rewardsPaymentInRewardsCurrency,
    userSelectedPaymentMethod,
    totalCreditCardPaymentRequired,
    quotedRewardsTotal,
    travelWalletItemsToApply,
    creditToApplyAmount,
    offerToApplyAmount,
    chfarExerciseDiscountAmount,
    isMultiRewardsMode,
    rewardsAllocations,
    rewardsAccounts
  ): ISummaryLineItem[] => {
    const isInChfarExercise =
      chfarExerciseDiscountAmount && chfarExerciseDiscountAmount?.value > 0;
    const lineItems: ISummaryLineItem[] = [];
    const activeRewardsAccount =
      rewardsPaymentAccount ?? rewardsAccountWithLargestValue;

    if (flightTotalInPrices) {
      lineItems.push({
        type: "total",
        fiatPrice: flightTotalInPrices.fiat,
        rewardsPrice:
          activeRewardsAccount?.allowRewardsRedemption ?? true
            ? quotedRewardsTotal || flightTotalInPrices.rewards
            : undefined,
      });
    }
    if (isInChfarExercise) {
      lineItems.push({
        type: "custom",
        label: "Change your flight for any reason applied:",
        fiatPrice: {
          ...chfarExerciseDiscountAmount,
          value:
            Math.min(
              flightTotalInPrices?.fiat.value ?? 0,
              chfarExerciseDiscountAmount.value
            ) * -1,
        },
        className: "chfar-exercise-amount",
      });
    }
    if (
      flightTotalInPrices &&
      offerToApplyAmount &&
      travelWalletItemsToApply.offerToApply
    ) {
      lineItems.push({
        type: "custom",
        icon: "offer-tag",
        label: textConstants.TRAVEL_OFFER_APPLIED,

        fiatPrice: {
          currencyCode: travelWalletItemsToApply.offerToApply.amount.currency,
          currencySymbol: getCurrencySymbol(
            travelWalletItemsToApply.offerToApply.amount.currency
          ),
          value: offerToApplyAmount * -1,
        },
        className: "travel-wallet-line",
      });
    }
    if (
      flightTotalInPrices &&
      creditToApplyAmount &&
      travelWalletItemsToApply.creditToApply
    ) {
      const breakdownHasStatementCredit =
        !!travelWalletItemsToApply.creditToApply.breakdown?.some(
          (detail) =>
            detail.CreditDetail === "Statement" &&
            Math.abs(detail.usableAmount.amount) > 0
        );
      lineItems.push({
        type: "custom",
        icon: "piggy-bank-icon",
        label: textConstants.TRAVEL_CREDITS_APPLIED,
        fiatPrice: {
          currencyCode: travelWalletItemsToApply.creditToApply.amount.currency,
          currencySymbol: getCurrencySymbol(
            travelWalletItemsToApply.creditToApply.amount.currency
          ),
          value: creditToApplyAmount * -1,
        },
        className: "travel-wallet-line",
        travelCreditBreakdown: breakdownHasStatementCredit
          ? getCheckoutCreditBreakdown(
            travelWalletItemsToApply.creditToApply.breakdown || [],
            creditToApplyAmount,
            travelWalletItemsToApply.creditToApply.amount.currency
          )
          : [],
      });
    }

    if (isMultiRewardsMode) {
      rewardsAllocations
        .filter((allocation) => allocation.fiat.value > 0)
        .forEach((allocation) => {
          const account = rewardsAccounts.find(
            (rewardsAccount) =>
              rewardsAccount.accountReferenceId ===
              allocation.accountReferenceId
          );
          lineItems.push({
            type: "rewards",
            fiatPrice: allocation.fiat,
            rewardsAccountName: account?.productDisplayName ?? "",
            rewardsPrice: allocation.rewards,
          });
        });
    } else if (
      activeRewardsAccount &&
      rewardsPaymentAccountReferenceId &&
      (rewardsPaymentInFiatCurrency?.value || 0) > 0
    ) {
      lineItems.push({
        type: "rewards",
        fiatPrice: rewardsPaymentInFiatCurrency || {
          ...currency,
          value: 0,
        },
        rewardsAccountName: activeRewardsAccount.productDisplayName,
        rewardsPrice: rewardsPaymentInRewardsCurrency || {
          currency: activeRewardsAccount.rewardsBalance.currency,
          value: 0,
        },
      });
    }
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply ||
      (activeRewardsAccount && rewardsPaymentAccountReferenceId) ||
      (isMultiRewardsMode && rewardsAllocations.length > 0)
    ) {
      lineItems.push({
        type: "payment",
        fiatPrice: totalCreditCardPaymentRequired,
        lastFour: userSelectedPaymentMethod?.last4,
      });
    }

    return lineItems;
  }
);

export const getIsTravelWalletOfferPaymentOnly = createSelector(
  getPricing,
  shopPricingInfoSelector,
  getPaymentTotalInPrices,
  getTravelWalletItemsToApply,
  getPollPriceQuoteCallState,
  getPriceQuoteErrors,
  getPriceDifferenceAcknowledged,
  getChfarExerciseDiscountAmountFiat,
  (
    tripPricing,
    shopPricing,
    total,
    travelWalletItemsToApply,
    priceQuoteCallState,
    priceQuoteErrors,
    priceDiffAcknowledged,
    chfarExerciseDiscountAmountFiat
  ) => {
    if (
      travelWalletItemsToApply.offerToApply &&
      !travelWalletItemsToApply.creditToApply &&
      (priceQuoteCallState === CallState.Success || !!priceDiffAcknowledged) && // on price diff acknowledged, booking errors are reset, causing PQ callstate to also reset, so it checks if pricediff was acknowledged
      priceQuoteErrors.length < 1
    ) {
      const chfarExerciseDiscount = chfarExerciseDiscountAmountFiat?.value || 0;

      // The reason we're only subtracting the ChFAR discount from the shop pricing fare is because
      // the total value from getPaymentTotalInPrices already has the ChFAR discount applied
      return tripPricing?.totalPricing.total.fiat.value === 0
        ? Math.abs(travelWalletItemsToApply.offerToApply?.amount.amount) >=
        (shopPricing?.fare?.[0]?.pricing.total?.fiat.value ?? 0) -
        chfarExerciseDiscount
        : total?.fiat.value === 0;
    }
    return false;
  }
);

export const getIsTravelWalletCreditPaymentOnly = createSelector(
  getPricing,
  shopPricingInfoSelector,
  getPaymentTotalInPrices,
  getTravelWalletItemsToApply,
  getPollPriceQuoteCallState,
  getPriceQuoteErrors,
  getPriceDifferenceAcknowledged,
  getChfarExerciseDiscountAmountFiat,
  (
    tripPricing,
    shopPricing,
    total,
    travelWalletItemsToApply,
    priceQuoteCallState,
    priceQuoteErrors,
    priceDiffAcknowledged,
    chfarExerciseDiscountAmountFiat
  ) => {
    if (
      travelWalletItemsToApply.creditToApply &&
      !travelWalletItemsToApply.offerToApply &&
      (priceQuoteCallState === CallState.Success || !!priceDiffAcknowledged) && // on price diff acknowledged, booking errors are reset, causing PQ callstate to also reset, so it checks if pricediff was acknowledged
      priceQuoteErrors.length < 1
    ) {
      const chfarExerciseDiscount = chfarExerciseDiscountAmountFiat?.value || 0;

      // The reason we're only subtracting the ChFAR discount from the shop pricing fare is because
      // the total value from getPaymentTotalInPrices already has the ChFAR discount applied
      return tripPricing?.totalPricing.total.fiat.value === 0
        ? Math.abs(travelWalletItemsToApply.creditToApply?.amount.amount) >=
        (shopPricing?.fare?.[0]?.pricing.total?.fiat.value ?? 0) -
        chfarExerciseDiscount
        : total?.fiat.value === 0;
    }
    return false;
  }
);

export const getIsStackedTravelWalletPaymentOnly = createSelector(
  getPricing,
  shopPricingInfoSelector,
  getPaymentTotalInPrices,
  getTravelWalletItemsToApply,
  getPollPriceQuoteCallState,
  getPriceQuoteErrors,
  getPriceDifferenceAcknowledged,
  getChfarExerciseDiscountAmountFiat,

  (
    tripPricing,
    shopPricing,
    total,
    travelWalletItemsToApply,
    priceQuoteCallState,
    priceQuoteErrors,
    priceDiffAcknowledged,
    chfarExerciseDiscountAmountFiat
  ) => {
    if (
      travelWalletItemsToApply.creditToApply &&
      travelWalletItemsToApply.offerToApply &&
      (priceQuoteCallState === CallState.Success || !!priceDiffAcknowledged) && // on price diff acknowledged, booking errors are reset, causing PQ callstate to also reset, so it checks if pricediff was acknowledged
      priceQuoteErrors.length < 1
    ) {
      const chfarExerciseDiscount = chfarExerciseDiscountAmountFiat?.value || 0;

      // The reason we're only subtracting the ChFAR discount from the shop pricing fare is because
      // the total value from getPaymentTotalInPrices already has the ChFAR discount applied
      return tripPricing?.totalPricing.total.fiat.value === 0
        ? Math.abs(
          travelWalletItemsToApply.creditToApply.amount.amount +
          travelWalletItemsToApply.offerToApply.amount.amount
        ) >=
        (shopPricing?.fare?.[0]?.pricing.total?.fiat.value ?? 0) -
        chfarExerciseDiscount
        : total?.fiat.value === 0;
    }
    return false;
  }
);

export const getIsTravelWalletPaymentOnly = createSelector(
  getIsTravelWalletOfferPaymentOnly,
  getIsTravelWalletCreditPaymentOnly,
  getIsStackedTravelWalletPaymentOnly,
  (
    isTravelWalletOfferPaymentOnly,
    isTravelWalletCreditPaymentOnly,
    isStackedTravelWalletPaymentOnly
  ) => {
    if (
      isTravelWalletOfferPaymentOnly ||
      isTravelWalletCreditPaymentOnly ||
      isStackedTravelWalletPaymentOnly
    ) {
      return true;
    }
    return false;
  }
);

export enum Progress {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  COMPLETED = 2,
}

const getAddTravelersProgress = createSelector(
  getConfirmationEmail,
  getConfirmationPhoneNumber,
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  isFlightBookWithAncillariesActiveSelector,
  getFlightBookType,
  getPassengersValidErrors,
  (
    email,
    phoneNumber,
    travelers,
    lapInfants,
    isFlightBookWithAncillariesActive,
    flightBookType,
    passengersValidErrors
  ):
    | {
      status: Progress;
      name: string;
    }
    | undefined => {
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT: {
        return {
          name: isFlightBookWithAncillariesActive
            ? textConstants.ADD_TRAVELERS_SHORTENED
            : textConstants.ADD_TRAVELERS,
          status:
            travelers.length + lapInfants.length > 0 &&
              email &&
              phoneNumber &&
              emailRegex.test(email) &&
              phoneRegex.test(phoneNumber) &&
              passengersValidErrors.length === 0
              ? Progress.COMPLETED
              : Progress.IN_PROGRESS,
        };
      }
      default:
        return undefined;
    }
  }
);

const getSeatingProgress = createSelector(
  getSelectedSeats,
  getSeatMapHtml,
  getSkipSeatSelection,
  getFlightBookType,
  (
    seats,
    seatMapHtml,
    skipSeatSelection,
    flightBookType
  ):
    | {
      status: Progress;
      name: string;
    }
    | undefined => {
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT: {
        return {
          name: textConstants.SEATING,
          status:
            (skipSeatSelection && Progress.COMPLETED) || !!seatMapHtml
              ? seats.length > 0 || skipSeatSelection
                ? Progress.COMPLETED
                : Progress.IN_PROGRESS
              : Progress.NOT_STARTED,
        };
      }
      default:
        return undefined;
    }
  }
);

export const hasCfarBeenAttachedSelector = createSelector(
  selectedCfarIdSelector,
  (selectedCfarId): boolean => {
    if (selectedCfarId) {
      return (
        selectedCfarId.policyId !== DO_NOT_APPLY_OPTION_KEY &&
        selectedCfarId.productId !== DO_NOT_APPLY_OPTION_KEY
      );
    }

    return false;
  }
);

export const isCfarSelectedSelector = createSelector(
  selectedCfarIdSelector,
  (selectedCfarId): boolean => {
    if (selectedCfarId) {
      return true;
    }
    return false;
  }
);

export const hasChfarBeenAttachedSelector = createSelector(
  selectedChfarIdSelector,
  (selectedChfarId): boolean => {
    if (selectedChfarId) {
      return selectedChfarId.quoteId !== DO_NOT_APPLY_OPTION_KEY;
    }

    return false;
  }
);

export const hasDisruptionProtectionBeenAttachedSelector = createSelector(
  selectedDisruptionProtectionIdSelector,
  (selectedDisruptionProtectionId): boolean => {
    if (selectedDisruptionProtectionId) {
      return (
        selectedDisruptionProtectionId.policyId !== DO_NOT_APPLY_OPTION_KEY &&
        selectedDisruptionProtectionId.productId !== DO_NOT_APPLY_OPTION_KEY
      );
    }

    return false;
  }
);

export const hasAddOnBeenAttachedSelector = createSelector(
  hasCfarBeenAttachedSelector,
  hasChfarBeenAttachedSelector,
  hasDisruptionProtectionBeenAttachedSelector,
  (
    hasCfarBeenAttached,
    hasChfarBeenAttached,
    hasDisruptionProtectionBeenAttached
  ): boolean =>
    hasCfarBeenAttached ||
    hasChfarBeenAttached ||
    hasDisruptionProtectionBeenAttached
);

export const hasSMSBeenSelected = (state: IStoreState) =>
  state.flightBook.disruptionProtectionAlertOptIn.SMS;

export const hasAppNotifBeenSelected = (state: IStoreState) =>
  state.flightBook.disruptionProtectionAlertOptIn.appNotif;

/*
  note: the isSeatSelectionGroupEnabled prop is a workaround to avoid adding the seat experiment to redux through the current pattern;
  once https://hopper-jira.atlassian.net/browse/BF-874 is handled, it will have to be addressed accordingly.
*/
export const getDpOptionSelectionProgressSelector = createSelector(
  isDpOptionSelectionCompleteSelector,
  isDisruptionProtectionAvailableSelector,
  getSeatingProgress,
  getAddTravelersProgress,
  getFlightBookType,
  (
    isDpOptionSelectionComplete,
    isDisruptionProtectionAvailable,
    seatingProgress,
    addTravelersProgress,
    flightBookType
  ): ((isSeatSelectionGroupEnabled: boolean) =>
    | {
      status: Progress;
      name: string;
    }
    | undefined) =>
    (isSeatSelectionGroupEnabled: boolean) => {
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT: {
          if (isDisruptionProtectionAvailable) {
            const dpStatus = (() => {
              if (isDpOptionSelectionComplete) {
                return Progress.COMPLETED;
              }
              if (
                (isSeatSelectionGroupEnabled &&
                  seatingProgress?.status === Progress.COMPLETED) ||
                (!isSeatSelectionGroupEnabled &&
                  addTravelersProgress?.status === Progress.COMPLETED)
              ) {
                return Progress.IN_PROGRESS;
              }
              return Progress.NOT_STARTED;
            })();

            return {
              name: textConstants.DISRUPTION_SUPPORT,
              status: dpStatus,
            };
          }
          return undefined;
        }
        default:
          return undefined;
      }
    }
);

export const getCfarOptionSelectionProgressSelector = createSelector(
  isCfarOptionSelectionCompleteSelector,
  isCfarAvailableSelector,
  isDisruptionProtectionAvailableSelector,
  getDpOptionSelectionProgressSelector,
  getSeatingProgress,
  getAddTravelersProgress,
  getFlightBookType,
  (
    isCfarOptionSelectionComplete,
    isCfarAvailable,
    isDisruptionProtectionAvailable,
    getDpOptionSelectionProgress,
    seatingProgress,
    addTravelersProgress,
    flightBookType
  ): ((isSeatSelectionGroupEnabled: boolean) =>
    | {
      status: Progress;
      name: string;
    }
    | undefined) =>
    (isSeatSelectionGroupEnabled: boolean) => {
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT: {
          if (isCfarAvailable) {
            const cfarStatus = (() => {
              if (isCfarOptionSelectionComplete) {
                return Progress.COMPLETED;
              }
              if (
                (isDisruptionProtectionAvailable &&
                  getDpOptionSelectionProgress(isSeatSelectionGroupEnabled)
                    ?.status === Progress.COMPLETED) ||
                (!isDisruptionProtectionAvailable &&
                  ((isSeatSelectionGroupEnabled &&
                    seatingProgress?.status === Progress.COMPLETED) ||
                    (!isSeatSelectionGroupEnabled &&
                      addTravelersProgress?.status === Progress.COMPLETED)))
              ) {
                return Progress.IN_PROGRESS;
              }
              return Progress.NOT_STARTED;
            })();

            return {
              name: textConstants.CFAR,
              status: cfarStatus,
            };
          }
          return undefined;
        }
        default:
          return undefined;
      }
    }
);

export type BookingProgress = {
  [key in CheckoutSteps]?: {
    status: Progress;
    name: string;
  };
};

export const getBookingProgress = createSelector(
  getAddTravelersProgress,
  getSeatingProgress,
  getDpOptionSelectionProgressSelector,
  getCfarOptionSelectionProgressSelector,
  getSelectedPaymentMethodId,
  getRewardsPaymentAccountReferenceId,
  getIsCreditCardPaymentRequired,
  isFlightBookWithAncillariesActiveSelector,
  getFlightBookType,
  getAllowRewardsWithPolicy,
  (
    addTravelersProgress,
    seatingProgress,
    getDpOptionSelectionProgress,
    getCfarOptionSelectionProgress,
    selectedPayment,
    selectedRewardsAccount,
    creditCardRequired,
    isFlightBookWithAncillariesActive,
    flightBookType,
    canRedeemRewards
  ): BookingProgress => {
    const paymentStatus = (() => {
      const unsettledPayment = !selectedPayment && creditCardRequired;

      if (selectedRewardsAccount && unsettledPayment) {
        return Progress.IN_PROGRESS;
      }
      if (!selectedRewardsAccount && !selectedPayment) {
        return Progress.NOT_STARTED;
      }
      return Progress.COMPLETED;
    })();

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT: {
        const progress = {
          [CheckoutSteps.REWARDS_AND_PAYMENT]: {
            name:
              isFlightBookWithAncillariesActive || !canRedeemRewards
                ? textConstants.REWARDS_AND_PAYMENT_SHORTENED
                : textConstants.REWARDS_AND_PAYMENT,
            status: paymentStatus,
          },
        };

        if (addTravelersProgress) {
          progress[CheckoutSteps.ADD_TRAVELERS] = addTravelersProgress;
        }

        if (seatingProgress) {
          progress[CheckoutSteps.SEATING] = seatingProgress;
        }

        if (isFlightBookWithAncillariesActive) {
          /*
            note: the seat selection experiment is 100% available on production; the isSeatSelectionGroupEnabled param is a workaround for now,
            once https://hopper-jira.atlassian.net/browse/BF-874 is handled, this will have to be addressed accordingly.
          */
          const dpOptionSelectionProgress = getDpOptionSelectionProgress(true);
          const cfarOptionSelectionProgress =
            getCfarOptionSelectionProgress(true);

          if (dpOptionSelectionProgress) {
            progress[CheckoutSteps.DISRUPTION_SUPPORT] =
              dpOptionSelectionProgress;
          }
          if (cfarOptionSelectionProgress) {
            progress[CheckoutSteps.CFAR] = cfarOptionSelectionProgress;
          }
        }

        return progress;
      }
      default:
        return {};
    }
  }
);

export const getBookingProgressList = createSelector(
  getBookingProgress,
  getFlightBookType,
  (
    bookingProgress,
    flightBookType
  ): {
    status: Progress;
    name: string;
    type: CheckoutSteps;
  }[] => {
    const bookingProgressList: {
      status: Progress;
      name: string;
      type: CheckoutSteps;
    }[] = [];

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        Object.keys(CheckoutSteps).map((key) => {
          const value = CheckoutSteps[key];
          const statusName = bookingProgress[value];
          if (statusName) {
            bookingProgressList.push({
              ...statusName,
              type: value,
            });
          }
        });
        break;
      default:
        break;
    }

    return bookingProgressList;
  }
);

export const getCheckoutStepNumberSelector = createSelector(
  getBookingProgressList,
  (bookingProgressList): ((step: CheckoutSteps) => number) =>
    (step: CheckoutSteps) =>
      bookingProgressList.findIndex((progress) => progress.type === step) + 1
);

// note: when a different order of checkout steps needs to be implemented,
// simply change the enum here, which will sort by their order in the enum.
export enum CheckoutSteps {
  ADD_TRAVELERS,
  SEATING,
  DISRUPTION_SUPPORT,
  CFAR,
  REWARDS_AND_PAYMENT,
}

export const isTravelerStepCompleteSelector = createSelector(
  getBookingProgress,
  getFlightBookType,
  getChfarExerciseDiscountAmountFiat,
  (checkoutProgress, flightBookType, chfarExerciseDiscountAmount) => {
    // during chfar exercise flow, the traveler step is always complete (since we cannot change the travelers)
    if (chfarExerciseDiscountAmount && chfarExerciseDiscountAmount.value > 0) {
      return true;
    }
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        return (
          checkoutProgress[CheckoutSteps.ADD_TRAVELERS]?.status ===
          Progress.COMPLETED
        );
      default:
        return false;
    }
  }
);

export const areAllStepsCompletedInCheckout = createSelector(
  getBookingProgress,
  getFlightBookType,
  getIsTravelWalletPaymentOnly,
  (checkoutProgress, flightBookType, isTravelWalletPaymentOnly) => {
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        return (
          checkoutProgress[CheckoutSteps.ADD_TRAVELERS]?.status ===
          Progress.COMPLETED &&
          (!isTravelWalletPaymentOnly
            ? checkoutProgress[CheckoutSteps.REWARDS_AND_PAYMENT]?.status ===
            Progress.COMPLETED
            : true)
        );
      default:
        return false;
    }
  }
);

export const getIsChfarExercise = createSelector(
  getChfarExerciseDiscountAmountFiat,
  (chfarExerciseAmount) => chfarExerciseAmount && chfarExerciseAmount.value > 0
);

export const chfarExerciseTrackingPropertiesSelector = createSelector(
  getIsChfarExercise,
  getChfarExerciseTrackingProperties,
  (isInChfarExercise, chfarExerciseTrackingProperties) => {
    if (!isInChfarExercise) {
      return null;
    }
    return chfarExerciseTrackingProperties != null
      ? chfarExerciseTrackingProperties
      : { properties: {}, encryptedProperties: [] };
  }
);

/**
 * This is a selector to check if the flight cost is fully covered by the ChFAR coverage amount
 */
export const isChfarDiscountPaymentOnlySelector = createSelector(
  getIsChfarExercise,
  getFlightTotal,
  getChfarExerciseDiscountAmountFiat,
  (isChfarExercise, flightTotal, chfarExerciseDiscount) => {
    if (isChfarExercise && flightTotal && chfarExerciseDiscount?.value) {
      return flightTotal <= chfarExerciseDiscount.value;
    }
    return false;
  }
);

export const getIsBookingValid = createSelector(
  getPassengersValid,
  getPriceDifference,
  getPriceDifferenceAcknowledged,
  getIsCreditCardPaymentRequired,
  getSelectedPaymentMethodId,
  getUserSelectedPassengerIds,
  bookingInProgressSelector,
  isTravelerStepCompleteSelector,
  getFlightBookType,
  getIsTravelWalletPaymentOnly,
  isChfarDiscountPaymentOnlySelector,
  (
    passengersValid,
    priceDifference,
    priceDifferenceAcknowledged,
    creditCardRequired,
    selectedPaymentMethodId,
    travelerIds,
    bookingInProgress,
    isTravelerStepComplete,
    flightBookType,
    isTravelWalletPaymentOnly,
    isChfarDiscountPaymentOnly
  ) => {
    const paymentUnsettled = isChfarDiscountPaymentOnly
      ? false
      : creditCardRequired &&
      !selectedPaymentMethodId &&
      !isTravelWalletPaymentOnly;

    let isFlightBookReady: boolean;

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        isFlightBookReady = !!passengersValid && travelerIds.length >= 1;
        break;
      default:
        isFlightBookReady = true;
        break;
    }

    if (
      (priceDifference.hasDifference && !priceDifferenceAcknowledged) ||
      paymentUnsettled ||
      bookingInProgress ||
      !isTravelerStepComplete ||
      !isFlightBookReady
    ) {
      return false;
    }
    return true;
  }
);

export const getEarn = (state: IStoreState) =>
  state.flightBook.productEarnValue;

export const getEarnByRewardsAccountId = (state: IStoreState) =>
  state.flightBook.earnValuesByRewardAcctId;

const getPaymentRequestType = createSelector(
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getIsMultiRewardsMode,
  getRewardsAllocations,
  (
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    selectedPaymentMethodId,
    isMultiRewardsMode,
    rewardsAllocations
  ) => {
    const hasMultiRewardsApplied =
      isMultiRewardsMode &&
      rewardsAllocations.some((allocation) => allocation.fiat.value > 0);

    if (selectedPaymentMethodId && !rewardsPayment && !hasMultiRewardsApplied) {
      return PaymentSplitRequestEnum.PaymentCardRequest;
    }
    if (
      hasMultiRewardsApplied &&
      creditCardPayment.value === 0
    ) {
      return PaymentSplitRequestEnum.PaymentRewardsRequest;
    }
    if (hasMultiRewardsApplied) {
      return PaymentSplitRequestEnum.PaymentCardRewardsRequest;
    }
    if (
      rewardsAccountId &&
      rewardsPayment &&
      rewardsFiatPayment &&
      creditCardPayment.value === 0
    ) {
      return PaymentSplitRequestEnum.PaymentRewardsRequest;
    }
    if (rewardsPayment && rewardsAccountId && rewardsFiatPayment) {
      return PaymentSplitRequestEnum.PaymentCardRewardsRequest;
    }
    return null;
  }
);

export const getEarnedString = createSelector(
  getEarn,
  getCardPaymentRewardsAccount,
  getAllowRewardsWithPolicy,
  (earn, account, canRedeemRewards) => {
    if (!earn || !canRedeemRewards) return "";
    return textConstants.EARNED_STRING(
      earn,
      account?.rewardsBalance.currencyDescription
    );
  }
);

export const getPaymentRequest = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getSession,
  getSelectedSeats,
  getFlightBookType,
  getIsMultiRewardsMode,
  getRewardsAllocations,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    session,
    selectedSeats,
    flightBookType,
    isMultiRewardsMode,
    rewardsAllocations
  ): PaymentSplitRequest | null => {
    const primaryRewardsAllocation =
      isMultiRewardsMode && rewardsAllocations.length
        ? rewardsAllocations.reduce((largest, current) =>
          current.fiat.value > largest.fiat.value ? current : largest
        )
        : undefined;

    const effectiveRewardsAccountId = isMultiRewardsMode
      ? primaryRewardsAllocation?.accountReferenceId
      : rewardsAccountId;
    const effectiveRewardsFiatPayment = isMultiRewardsMode
      ? primaryRewardsAllocation?.fiat
      : rewardsFiatPayment;
    const effectiveRewardsPayment = isMultiRewardsMode
      ? primaryRewardsAllocation?.rewards
      : rewardsPayment;

    let amount: PaymentType | null = null;
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        const cardPaymentType: UserCardPaymentType = {
          accountReferenceId,
          paymentId: paymentId || "",
          paymentAmount: {
            currency: creditCardPayment.currencyCode,
            amount: roundToTwoDecimals(creditCardPayment.value),
            PaymentAmount: PaymentAmountEnum.FiatAmount,
          },
          Payment: TypeOfPaymentEnum.UserCard,
        };
        amount = cardPaymentType;
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        const splitPaymentType: SplitPaymentType = {
          paymentId: paymentId || "",
          accountReferenceId,
          paymentAmount: {
            fiatAmount: {
              currency: creditCardPayment.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment.value),
              PaymentAmount: PaymentAmountEnum.FiatAmount,
            },
            rewardsAmount: {
              rewardsAccountId: effectiveRewardsAccountId!,
              fiatValue: {
                amount: roundToTwoDecimals(effectiveRewardsFiatPayment!.value),
                currency: effectiveRewardsFiatPayment!.currencyCode,
              },
              rewardsPrice: {
                value: effectiveRewardsPayment!.value,
                currency: effectiveRewardsPayment!.currency,
              },
              PaymentAmount: PaymentAmountEnum.RewardsAmount,
            },
            PaymentAmount: PaymentAmountEnum.SplitAmount,
          },
          Payment: TypeOfPaymentEnum.Split,
        };
        amount = splitPaymentType;
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        const rewardsPaymentType: RewardsPaymentType = {
          paymentAmount: {
            rewardsAccountId: effectiveRewardsAccountId!,
            fiatValue: {
              amount: roundToTwoDecimals(effectiveRewardsFiatPayment!.value),
              currency: effectiveRewardsFiatPayment!.currencyCode,
            },
            rewardsPrice: {
              value: roundToTwoDecimals(effectiveRewardsPayment!.value),
              currency: effectiveRewardsPayment!.currency,
            },
            PaymentAmount: PaymentAmountEnum.RewardsAmount,
          },
          Payment: TypeOfPaymentEnum.Rewards,
        };
        amount = rewardsPaymentType;
        break;
      default:
        return null;
    }

    if (amount && session) {
      const ancillaries: PostQuoteAncillaries[] = [];
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
          if (selectedSeats.length > 0) {
            ancillaries.push({
              seats: {
                seats: selectedSeats,
              },
              PostQuoteAncillaries: PostQuoteAncillariesEnum.Seats,
            });
          }
          break;
        default:
          break;
      }

      const request = {
        token: session,
        payment: amount,
        ancillaries,
      };
      return request;
    }
    return null;
  }
);

export const getDisruptionProtectionRebookPayments = createSelector(
  getPriceQuoteWithUpdatedAncillary,
  singleFlightItineraryIdSelector,
  (priceQuote, singleFlightItineraryId): PaymentOpaqueValue[] | null => {
    const totalPrices = priceQuote?.itinerary.sellingPricing.totalPricing.total;
    const totalFiat = totalPrices?.fiat;

    if (totalFiat && singleFlightItineraryId) {
      return [
        {
          type: Payment.DisruptionProtectionCredit,
          value: {
            paymentAmount: {
              currency: totalFiat.currencyCode,
              amount: roundToTwoDecimals(totalFiat.value),
            },
            originalItineraryId: singleFlightItineraryId,
            PaymentV2: PaymentV2Enum.DisruptionProtectionCredit,
          },
        },
      ];
    }

    return null;
  }
);

export const getPaymentsV2 = createSelector(
  getPaymentMethodRewardsAccountId,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getRewardsPaymentInRewardsCurrency,
  getRewardsPaymentInFiatCurrency,
  getRewardsPaymentAccountReferenceId,
  getSelectedPaymentMethodId,
  getPaymentRequestType,
  getTravelWalletItemsToApply,
  getTravelWalletOfferToApplyAmount,
  getTravelWalletCreditToApplyAmount,
  isChfarDiscountPaymentOnlySelector,
  selectChfarExerciseContractId,
  getIsMultiRewardsMode,
  getRewardsAllocations,
  (
    accountReferenceId,
    creditCardPayment,
    rewardsPayment,
    rewardsFiatPayment,
    rewardsAccountId,
    paymentId,
    paymentRequestType,
    travelWalletItemsToApply,
    offerToApplyAmount,
    creditToApplyAmount,
    isChfarDiscountPaymentOnly,
    chfarExerciseContractId,
    isMultiRewardsMode,
    rewardsAllocations
  ): PaymentOpaqueValue[] | null => {
    const amount: PaymentOpaqueValue[] = [];

    const multiRewardsEntries = buildMultiRewardsPaymentEntries(rewardsAllocations);

    // Although the cost is fully covered by the ChFAR discount, the V0 schedule fulfill endpoint requires a payment method to be passed in.
    // The cart should already have this payment method added from schedule quote, but we need to add it here to ensure a payment method is always present for the V0 schedule fulfill endpoint.
    if (isChfarDiscountPaymentOnly && chfarExerciseContractId) {
      amount.push({
        type: Payment.AirChfarExerciseDiscount,
        value: {
          contractId: chfarExerciseContractId,
        },
      } satisfies PaymentOpaqueValue);
    }
    if (
      travelWalletItemsToApply.offerToApply ||
      travelWalletItemsToApply.creditToApply
    ) {
      if (travelWalletItemsToApply.offerToApply) {
        amount.push({
          type: Payment.TravelWalletOffer,
          value: {
            offerId: travelWalletItemsToApply.offerToApply.id,
            description:
              travelWalletItemsToApply.offerToApply?.descriptions[0] || "",
            paymentAmount: {
              fiatValue: {
                amount: offerToApplyAmount,
                currency: travelWalletItemsToApply.offerToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletOffer,
          } as any,
        });
      }
      if (travelWalletItemsToApply.creditToApply && creditToApplyAmount > 0) {
        amount.push({
          type: Payment.TravelWalletCredit,
          value: {
            offerId: travelWalletItemsToApply.creditToApply.id,
            description: "TravelWalletCredit", // creditToApply doesn't have a description
            paymentAmount: {
              fiatValue: {
                amount: roundToTwoDecimals(creditToApplyAmount),
                currency:
                  travelWalletItemsToApply.creditToApply.amount.currency,
              },
            },
            PaymentV2: PaymentV2Enum.TravelWalletCredit,
          } as any,
        });
      }
    }
    switch (paymentRequestType) {
      case PaymentSplitRequestEnum.PaymentCardRequest:
        amount.push({
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          } as any,
        });
        break;
      case PaymentSplitRequestEnum.PaymentCardRewardsRequest:
        amount.push({
          type: Payment.Card,
          value: {
            paymentId: paymentId || "",
            accountReferenceId,
            paymentAmount: {
              currency: creditCardPayment!.currencyCode,
              amount: roundToTwoDecimals(creditCardPayment!.value),
            },
            PaymentV2: PaymentV2Enum.UserCard,
          } as any,
        });
        if (isMultiRewardsMode) {
          amount.push(...multiRewardsEntries);
        } else {
          amount.push({
            type: Payment.Rewards,
            value: {
              paymentAmount: {
                rewardsAccountId,
                fiatValue: {
                  amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                  currency: rewardsFiatPayment!.currencyCode,
                },
                rewardsPrice: {
                  value: roundToTwoDecimals(rewardsPayment!.value),
                  currency: rewardsPayment!.currency,
                },
              },
              PaymentV2: PaymentV2Enum.Rewards,
            } as any,
          });
        }
        break;
      case PaymentSplitRequestEnum.PaymentRewardsRequest:
        if (isMultiRewardsMode) {
          amount.push(...multiRewardsEntries);
        } else {
          amount.push({
            type: Payment.Rewards,
            value: {
              paymentAmount: {
                rewardsAccountId,
                fiatValue: {
                  amount: roundToTwoDecimals(rewardsFiatPayment!.value),
                  currency: rewardsFiatPayment!.currencyCode,
                },
                rewardsPrice: {
                  value: roundToTwoDecimals(rewardsPayment!.value),
                  currency: rewardsPayment!.currency,
                },
              },
              PaymentV2: PaymentV2Enum.Rewards,
            } as any,
          });
        }
        break;
      default:
        break;
    }
    return amount;
  }
);

export const getAncillaries = createSelector(
  getSelectedSeats,
  getFlightBookType,
  (selectedSeats, flightBookType): AncillaryOpaqueValue[] | null => {
    const ancillaries: AncillaryOpaqueValue[] = [];
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        if (selectedSeats.length > 0) {
          ancillaries.push({
            type: Ancillary.Seats,
            value: {
              seats: selectedSeats,
            } as any,
          });
        }
        break;
      default:
        break;
    }
    return ancillaries;
  }
);

export const getCardPaymentAccount = createSelector(
  getRewardsAccounts,
  getPaymentRequest,
  (rewardsAccounts, paymentRequest: PaymentSplitRequest | null) => {
    switch (paymentRequest?.payment.Payment) {
      case TypeOfPaymentEnum.UserCard:
        return rewardsAccounts.find(
          (acc) =>
            acc.accountReferenceId ===
            (paymentRequest?.payment as UserCardPaymentType).accountReferenceId
        );
      case TypeOfPaymentEnum.Split:
        return rewardsAccounts.find(
          (acc) =>
            acc.accountReferenceId ===
            (paymentRequest?.payment as SplitPaymentType).accountReferenceId
        );
      default:
        return null;
    }
  }
);
export const rewardsAccountMinimumRequirementStateSelector = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccountMinimumRequirementState =>
    getRewardsAccountMinimumRequirementState(rewardsAccounts)
);

export const getRewardsAccountsWithPaymentAccounts = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  (rewardsAccounts, paymentMethods) => {
    const accountsWithSavedPayment = paymentMethods.reduce(
      (savedPayments, currentAcc) => {
        const matchingRewardsAcct = rewardsAccounts.find(
          (rewards) =>
            rewards.lastFour === currentAcc.last4 ||
            rewards.lastFourVirtualCardNumbers?.includes(currentAcc.last4 || "")
        );

        if (matchingRewardsAcct) {
          savedPayments.push(matchingRewardsAcct);
        }
        const isTestCard =
          window.__mclean_env__.ENV !== "production" &&
          currentAcc.last4 &&
          TEST_CARD_LAST_FOURS.includes(currentAcc.last4);
        if (isTestCard) {
          savedPayments.push(rewardsAccounts[0]);
        }

        return savedPayments;
      },
      [] as RewardsAccount[]
    );
    return accountsWithSavedPayment;
  }
);

export const getInsuranceBaseTrackingProperties = createSelector(
  getRewardsAccountsWithPaymentAccounts,
  hasCfarBeenAttachedSelector,
  hasDisruptionProtectionBeenAttachedSelector,
  (
    rewardsAccountsWithPaymentAccounts,
    hasCfarBeenAttached,
    hasDisruptionProtectionBeenAttached
  ): InsuranceBaseProperties => ({
    funnel: "flights",
    card_products: rewardsAccountsWithPaymentAccounts.map(
      (account) => account?.productDisplayName
    ),
    cfar_attached: hasCfarBeenAttached,
    fda_attached: hasDisruptionProtectionBeenAttached,
  })
);

export const getTripFinalFlightDate = createSelector(
  selectedTripSelector,
  tripDetailsByIdSelector,
  (selectedTrip, tripDetailsById): string => {
    const selectedTripId = selectedTrip?.tripId || "";
    const trip = tripDetailsById[selectedTripId];
    const lastFlightIndex = trip.slices.length - 1;
    const latestFlightDate = trip.slices[lastFlightIndex].departureTime;
    return latestFlightDate;
  }
);

export const getTripFirstFlightDate = createSelector(
  selectedTripSelector,
  tripDetailsByIdSelector,
  (selectedTrip, tripDetailsById): string => {
    const selectedTripId = selectedTrip?.tripId || "";
    const trip = tripDetailsById[selectedTripId];
    return trip?.slices?.[0]?.departureTime ?? "";
  }
);

const ADULT_MINIMUM_AGE = 18;

export const getHasSelectedAdultPassenger = createSelector(
  getUserPassengers,
  getUserSelectedPassengerIds,
  getTripFirstFlightDate,
  (travelers, selectedPassengerIds, tripFirstFlightDate): boolean =>
    selectedPassengerIds.some((id) => {
      const traveler = travelers.find((t) => t.id === id);
      if (!traveler) return false;
      const ageAtBoardingTime = dayjs(tripFirstFlightDate).diff(
        getDateTimeWithFormat(traveler.dateOfBirth, "YYYY-MM-DD"),
        "year"
      );
      return ageAtBoardingTime >= ADULT_MINIMUM_AGE;
    })
);


interface ReviewFlightDetailsWithAddOnProperties
  extends ReviewFlightDetailsProperties,
  CfarAttachProperties { }

export const getReviewFlightDetailsProperties = createSelector(
  getViewedTripSummaryProperties,
  selectedTripSelector,
  tripDetailsByIdSelector,
  getCfarAttachProperties,
  dpOfferPropertiesSelector,
  getTripCategory,
  cfarDiscountPropertiesSelector,
  getCorporateInfo,
  getShopExperiments,
  getPotentialCrossSellOffers,
  getPaymentMethods,
  (
    viewedTripSummaryProperties,
    selectedTrip,
    tripDetailsById,
    cfarAttachProperties,
    dpOfferProperties,
    tripCategory,
    cfarDiscountProperties,
    corporateInfo,
    experiments,
    potentialCrossSellOffers,
    paymentMethods
  ): ReviewFlightDetailsWithAddOnProperties => {
    const trip = tripDetailsById[selectedTrip?.tripId!];
    if (trip && trip.slices.length > 0) {
      const stops = trip.slices.reduce(
        (stops, slice) => stops + slice.stops,
        0
      );
      const selectedOWRTrip = selectedTrip as ISelectedTrip;
      const selectedMcTrip = selectedTrip as ISelectedMulticityTrip;
      const fare = trip.fareDetails.find(
        (f) =>
          f.id === selectedOWRTrip.outgoingFareId ||
          f.id === selectedOWRTrip.returnFareId ||
          f.id === selectedMcTrip.departureFareId
      );
      const brand: FareBrand | undefined =
        fare &&
          fare.slices.length > 0 &&
          fare.slices[0].fareDetails.segments.length > 0
          ? fare?.slices[0].fareDetails.segments[0].brand
          : undefined;
      const carrier: string | undefined =
        fare &&
          fare.slices.length > 0 &&
          fare.slices[0].fareDetails.segments.length > 0
          ? fare?.slices[0].fareDetails.segments[0].validatingCarrierCode
          : undefined;
      let provider = "";
      if (brand) {
        if (brand.sabreFareBrand) {
          provider = "Sabre";
        } else if (brand.travelportFareBrand) {
          provider = "Travelport";
        } else if (brand.amadeusFareBrand) {
          provider = "Amadeus";
        } else if (brand.gdxFareBrand) {
          provider = "GDX";
        } else if (brand.travelFusionFareBrand) {
          provider = "TravelFusion";
        }
      }

      const isHotelCrossSellV3Experiment =
        experiments?.[HOTEL_CROSS_SELL_V3_EXPERIMENT] !== CONTROL;

      return {
        stops,
        provider,
        advance: dayjs(trip.slices[0].departureTime).diff(dayjs(), "day"),
        ...viewedTripSummaryProperties,
        ...cfarAttachProperties,
        ...dpOfferProperties,
        carrier: (viewedTripSummaryProperties.carrier || carrier) ?? "",
        departure_date: dayjs(trip.slices[0].departureTime).format(
          "YYYY-MM-DD"
        ),
        destination: viewedTripSummaryProperties.destination
          ? viewedTripSummaryProperties.destination
          : trip.slices[0].destinationName,
        destination_country_code:
          viewedTripSummaryProperties.destination_country_code
            ? viewedTripSummaryProperties.destination_country_code
            : trip.slices[0].destinationCode,
        fare_class: fare
          ? getHopperFareRatingName(fare?.slices[0].fareShelf?.rating)
          : "",
        origin: viewedTripSummaryProperties.origin
          ? viewedTripSummaryProperties.origin
          : trip.slices[0].originName,
        origin_country_code: viewedTripSummaryProperties.origin_country_code
          ? viewedTripSummaryProperties.origin_country_code
          : trip.slices[0].originCode,
        return_date:
          trip.slices.length > 1
            ? dayjs(trip.slices[1].departureTime).format("YYYY-MM-DD")
            : "",
        trip_type: tripCategory,
        is_multi_ticket: viewedTripSummaryProperties.is_multi_ticket,
        multi_ticket_type: viewedTripSummaryProperties.multi_ticket_type,
        business_loyalty_programs:
          corporateInfo?.businessLoyaltyPrograms?.join(", "),
        slice_count: trip.slices.length,
        is_same_day_round_trip:
          tripCategory === TripCategory.ROUND_TRIP &&
          trip.slices.length > 1 &&
          dayjs(trip.slices[0].departureTime).isSame(
            dayjs(trip.slices[1].departureTime),
            "day"
          ),
        cross_sell_displayed:
          isHotelCrossSellV3Experiment && potentialCrossSellOffers.length > 0,
        card_on_file: paymentMethods.length > 0,
        ...{
          cfar_discount_original_premium:
            cfarDiscountProperties.original_premium,
          cfar_discount_percentage: cfarDiscountProperties.discount_percentage,
        },
      };
    }
    return {
      stops: 0,
      provider: "",
      advance: 0,
      ...viewedTripSummaryProperties,
      carrier: viewedTripSummaryProperties.carrier || "",
      business_loyalty_programs:
        corporateInfo?.businessLoyaltyPrograms?.join(", "),
      slice_count: undefined,
      is_same_day_round_trip: undefined,
    };
  }
);

export const getViewItineraryRestrictionsProperties = createSelector(
  getReviewFlightDetailsProperties,
  selectedTripSelector,
  tripDetailsByIdSelector,
  (reviewFlightDetailsProperties, selectedTrip, tripDetailsById): any => {
    const selectedTripId = selectedTrip?.tripId || "";
    const trip = tripDetailsById[selectedTripId];
    if (!trip) {
      return {};
    }
    const selectedOWRTrip = selectedTrip as ISelectedTrip;
    const selectedMcTrip = selectedTrip as ISelectedMulticityTrip;
    const fare = trip.fareDetails.find(
      (f) =>
        f.id === selectedOWRTrip.outgoingFareId ||
        f.id === selectedOWRTrip.returnFareId ||
        f.id === selectedMcTrip.departureFareId
    );
    const getRestriction = (uta: DullesUta) =>
      uta.headline == "Checked or Carry-on Bags"
        ? `${uta.headline} - ${uta.description}`
        : `${uta.description}`;
    const outboundRestrictions =
      fare?.slices[0].amenitiesUtas?.utas.utas.reduce((restrictions, uta) => {
        restrictions[`outbound_${uta.category}`] = getRestriction(uta);
        return restrictions;
      }, {});
    const returnRestrictions =
      fare?.slices.length && fare.slices.length > 1
        ? fare?.slices[1].amenitiesUtas?.utas.utas.reduce(
          (restrictions, uta) => {
            restrictions[`return_${uta.category}`] = getRestriction(uta);
            return restrictions;
          },
          {}
        )
        : {};
    return {
      ...reviewFlightDetailsProperties,
      ...outboundRestrictions,
      ...returnRestrictions,
    };
  }
);

export const getSelectedPaymentCardType = createSelector(
  getRewardsAccounts,
  getPaymentMethods,
  getSelectedPaymentMethodId,
  (accounts, paymentMethods, id): string => {
    const paymentMethod = paymentMethods.find((p) => p.id === id);
    if (
      accounts[0]?.customerAccountRole === CustomerAccountRole.NonFinancialUser
    ) {
      return paymentMethod?.cardType ?? "";
    }
    return (
      accounts.find(
        (a) =>
          a.lastFour === paymentMethod?.last4 ||
          a.lastFourVirtualCardNumbers?.includes(paymentMethod?.last4 || "")
      )?.productDisplayName || ""
    );
  }
);

// The Pay Flex survey often renders before the customer picks a payment
// method, so fall back to the active/default rewards account to still report a
// card product.
export const getPayFlexCardProductUsed = createSelector(
  getSelectedPaymentCardType,
  getSelectedAccount,
  resolvePayFlexCardProduct
);

export const getNumberofTravelers = createSelector(
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  (pass, lapInfants) => pass.length + lapInfants.length
);

export const getPriceQuoteAirProperties = createSelector(
  getReviewFlightDetailsProperties,
  getNumberofTravelers,
  getSession,
  getSelectedFlightIndex,
  getPriceDifference,
  (
    reviewFlightDetailsProperties,
    numberOfTravelers,
    session,
    selectedFlightIndex,
    priceDifference
  ): PriceQuoteAirProperties | null => {
    if (!reviewFlightDetailsProperties) {
      return null;
    }

    return {
      success: false,
      ...reviewFlightDetailsProperties,
      pax_total: numberOfTravelers,
      booking_session_id: session?.value || "",
      flight_list_ranking: selectedFlightIndex || undefined,
      x_price_difference:
        parseInt(priceDifference?.amount ?? "0", 10) *
        (priceDifference?.isIncrease ? 1 : -1),
    };
  }
);

export const getIsPaymentMethodVCN = createSelector(
  getPaymentMethodRewardsAccountId,
  getPaymentMethod,
  getRewardsAccounts,
  (paymentRewardsAccountId, paymentMethod, rewardsAccounts) => {
    if (!paymentRewardsAccountId || !paymentMethod || !rewardsAccounts)
      return false;

    const account = rewardsAccounts.find(
      (account) => account.accountReferenceId === paymentRewardsAccountId
    );

    return !!(
      account?.lastFourVirtualCardNumbers &&
      paymentMethod?.last4 &&
      account?.lastFourVirtualCardNumbers?.includes(paymentMethod?.last4)
    );
  }
);

export const getFlightShopRecommendedBasedOnPreferences = (
  state: IStoreState
) => state.flightShop.recommendedBasedOnPreferences;

export const getUserInfo = (state: IStoreState) => state.flightRewards.userInfo;

export const getSelectedPassengers = createSelector(
  getUserPassengers,
  getUserSelectedPassengerIds,
  (passengers, ids): IPerson[] => passengers.filter((d) => ids.includes(d.id))
);

export const getCompleteBuyAirProperties = createSelector(
  getReviewFlightDetailsProperties,
  getRewardsPaymentInFiatCurrency,
  getTotalCreditCardPaymentRequiredInFiatPrice,
  getCardPaymentAccount,
  getRewardsPaymentInRewardsCurrency,
  getNumberofTravelers,
  getRewardsPaymentAccount,
  getPriceQuoteWithUpdatedAncillary,
  getSelectedSeats,
  getSeatsTotalPricing,
  getSelectedFlightIndex,
  getCreditToApply,
  getTravelWalletCreditToApplyAmount,
  getIsPaymentMethodVCN,
  getFlightShopEntryPoint,
  getFlightShopRecommendedBasedOnPreferences,
  getTripPurpose,
  getSelectedPassengers,
  getUserInfo,
  getPriceDifference,
  selectedTripDetailsSelector,
  selectedFareDetailsSelector,
  getCorporateInfo,
  getIsMultiRewardsMode,
  getRewardsAllocations,
  getRewardsAccounts,
  (
    reviewFlightDetailsProperties,
    rewardsPaymentFiat,
    creditCardPayment,
    paymentMethodRewardsAccount,
    rewardsPaymentCurrency,
    numberOfTravelers,
    rewardsPaymentAccount,
    priceQuote,
    seats,
    seatsPricing,
    selectedFlightIndex,
    creditToApply,
    creditAmountToApply,
    isPaymentMethodVCN,
    flightShopEntryPoint,
    recommendedBasedOnPreferences,
    tripPurpose,
    selectedPassengers,
    userInfo,
    priceDifference,
    selectedTripDetails,
    selectedFareDetails,
    corporateInfo,
    isMultiRewardsMode,
    rewardsAllocations,
    rewardsAccounts
  ): CompleteBuyAirProperties | null => {
    if (!reviewFlightDetailsProperties) {
      return null;
    }
    const isSelfBooked = selectedPassengers.some(
      (passenger: IPerson) =>
        (passenger as ICorpPerson).isDefaultProfile ??
        (passenger.givenName === userInfo?.firstName &&
          passenger.surname === userInfo?.lastName)
    );

    // Count travelers (not FF numbers) who have loyalty for booked airlines
    const bookedAirlineCodes = getBookedAirlineCodes(selectedTripDetails);
    const frequentFlyerAddedCount = selectedPassengers.filter((passenger) =>
      hasRelevantFrequentFlyer(passenger, bookedAirlineCodes)
    ).length;
    const matchedBusinessLoyaltyPrograms = getMatchedBusinessLoyaltyPrograms(
      corporateInfo?.businessLoyaltyPrograms,
      bookedAirlineCodes
    );

    const multiRewardsTrackingSummary = isMultiRewardsMode
      ? resolveMultiRewardsTrackingSummary(rewardsAllocations, rewardsAccounts)
      : undefined;

    return {
      success: false,
      total_rewards_amount_usd: isMultiRewardsMode
        ? multiRewardsTrackingSummary?.totalRewardsAmountUsd ?? 0
        : rewardsPaymentFiat?.value || 0,
      total_card_amount_usd: creditCardPayment.value || 0,
      card_product_used: paymentMethodRewardsAccount?.productDisplayName || "",
      rewards_product_used: isMultiRewardsMode
        ? multiRewardsTrackingSummary?.rewardsProductUsed ?? ""
        : rewardsPaymentAccount?.productDisplayName || "",
      rewards_currency: isMultiRewardsMode
        ? multiRewardsTrackingSummary?.rewardsCurrency ?? ""
        : rewardsPaymentCurrency?.currency || "",
      multi_rewards_accounts_used:
        multiRewardsTrackingSummary?.multiRewardsAccountsUsed,
      agent_book_fee_amount_usd: 0,
      agent_locator: "", // this is overwritten in the pollFinalizedSaga
      /*
        TODO: CFAR data from reviewFlightDetailsProperties gets expanded here;
        ideally, some of those properties should be overrode by the /purchase/pollFinalized response.
      */
      ...reviewFlightDetailsProperties,
      pax_total: numberOfTravelers,
      booking_session_id: "",
      seats_selection_available:
        priceQuote?.seatMap?.SeatMapResponse ===
        SeatMapResponseEnum.SeatMapAvailable,
      seats_selected_seats: seats?.length > 0,
      seats_selected_count: seats?.length,
      seats_selected_paid: seats?.filter((s) => s.price_object.total > 0)
        ?.length,
      seats_selected_free: seats?.filter((s) => s.price_object.total === 0)
        ?.length,
      seats_selected_price_usd: seatsPricing,
      lowest_price_seat: 0, // TODO add when cheapest seat work is in
      flight_list_ranking: selectedFlightIndex || undefined,
      credit_redeemed: !!creditToApply?.amount?.amount,
      credit_amt_redeemed:
        !!creditToApply && !!creditAmountToApply
          ? Math.abs(creditAmountToApply)
          : 0,
      is_vcn: isPaymentMethodVCN,
      entry_type: flightShopEntryPoint,
      preferences_recommended: recommendedBasedOnPreferences,
      purpose_of_travel: tripPurpose,
      booked_self_name_match: isSelfBooked,
      x_price_difference:
        parseInt(priceDifference?.amount ?? "0", 10) *
        (priceDifference?.isIncrease ? 1 : -1),
      ...(isCorpTenant(config.TENANT) && {
        frequent_flyer_added_count: frequentFlyerAddedCount,
        matched_business_loyalty_programs:
          matchedBusinessLoyaltyPrograms.join(", ") || undefined,
        is_corporate: getIsCorporateRateBooking(
          selectedTripDetails,
          selectedFareDetails
        ),
      }),
    };
  }
);

export const getPriceDropProperties = createSelector(
  getFlightBookPriceDropProtection,
  getPriceDropRefundTypeSelector,
  predictionSelector,
  (
    priceDropProtection,
    priceDropRefundType,
    prediction
  ): Omit<PriceDropViewedProperties, "page"> => {
    const eligible =
      priceDropProtection?.PriceDropProtection ===
      PriceDropProtectionEnum.IsEligible;
    return {
      price_drop_offer_duration: eligible
        ? (priceDropProtection as IsEligible).monitoringDuration.inSeconds
        : 0,
      price_drop_offer_max_cap: eligible
        ? (priceDropProtection as IsEligible).maximumRefund.amount.amount
        : 0,
      price_drop_offer_min_cap: eligible
        ? (priceDropProtection as IsEligible).minimumRefund.amount.amount
        : 0,
      price_bucket: prediction
        ? getPriceBucketValueFromDealness(prediction.dealness)
        : null,
      refund_type: eligible ? priceDropRefundType : null,
    };
  }
);

export const getIsPollQuoteOnly = (state: IStoreState) =>
  state.flightBook.pollQuoteOnly;

export const getIsConfirmAndBookReady = createSelector(
  getIsBookingValid,
  areAllStepsCompletedInCheckout,
  getIsPollQuoteOnly,
  (isBookingValid, allStepsCompleted, isPollQuoteOnly): boolean =>
    isBookingValid || (allStepsCompleted && !isPollQuoteOnly)
);

export const totalPassengersCountSelector = createSelector(
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  getFlightBookType,
  (selectedTravelers, selectedLapInfants, flightBookType): number => {
    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        return selectedTravelers.length + selectedLapInfants.length;
      default:
        return 1;
    }
  }
);

const getFlightBookErrorTitle = createSelector(
  getTripPricingErrors,
  getPassengersValidErrors,
  (tripPricingErrors, passengerErrors): ErrorTitles | null => {
    if (tripPricingErrors.length > 0) {
      return {
        icon: textConstants.UNABLED_ICON,
        title: tripPricingErrors[0].code,
        subtitle:
          tripPricingErrors[0].details || textConstants.GENERIC_ERROR_SUBTITLE,
        primaryButtonText: textConstants.UPDATE_TRAVELERS,
      };
    }

    if (passengerErrors.length > 0) {
      const passengerError = passengerErrors[0];
      let subtitle = passengerError.message || "";
      let title = "Invalid Passengers";
      let icon = textConstants.UNABLED_ICON;

      if (
        passengerError.code === "ADTRequired" &&
        isCorpTenant(config.TENANT)
      ) {
        subtitle = textConstants.ADT_REQUIRED_CORP_SUBTITLE;
      }

      if (
        passengerError.code === "PassportValidationFail" &&
        isCorpTenant(config.TENANT)
      ) {
        title = "Missing passport information";
        icon = textConstants.ERROR_ICON;
      }

      return {
        title,
        subtitle,
        primaryButtonText: textConstants.UPDATE_TRAVELERS,
        icon,
      };
    }

    return null;
  }
);

const getPriceQuoteAndFinalizedError = createSelector(
  getPriceQuoteErrors,
  getFinalizedErrors,
  totalPassengersCountSelector,
  getFlightBookType,
  getSelectedSeats,
  (
    priceQuoteErrors,
    finalizedErrors,
    totalPassengersCount,
    flightBookType,
    selectedSeats
  ) => {
    const errors =
      finalizedErrors.length > 0
        ? finalizedErrors
        : priceQuoteErrors.length > 0
          ? priceQuoteErrors
          : null;

    if (errors) {
      return getErrorTitlesFromPaymentError({
        errors,
        numTravelers: totalPassengersCount,
        flightBookType,
        hasSelectedSeats: selectedSeats.length > 0,
      });
    }

    return null;
  }
);

export const getErrorTitles = createSelector(
  getHasError,
  getPriceDifference,
  getSchedulePaymentError,
  getSchedulePriceQuoteError,
  getVerifyPaymentMethodResult,
  getFlightBookErrorTitle,
  getPriceQuoteAndFinalizedError,
  getFlightBookType,
  getInfantOnlyError,
  getTravelWalletItemsToApply,
  hasAddOnBeenAttachedSelector,
  getRewardsAccounts,
  (
    hasError,
    getPriceDifference,
    schedulePaymentError,
    schedulePriceQuoteError,
    verifyPaymentMethodResult,
    flightBookErrorTitle,
    priceQuoteAndFinalizedError,
    flightBookType,
    infantOnlyError,
    travelWalletItemsToApply,
    hasAddOnBeenAttached,
    accounts
  ): ErrorTitles => {
    if (!hasError) return { title: "", subtitle: "" };
    const {
      amount,
      amountPerTraveler,
      currency,
      hasDifference,
      isIncrease,
      predictedTotal,
      priceQuoteTotal,
    } = getPriceDifference;

    if (hasDifference && amount && amountPerTraveler) {
      const primaryButtonText = textConstants.CONTINUE;
      const offerToApplyAmount =
        travelWalletItemsToApply.offerToApply?.amount.amount || 0;
      const creditToApplyAmount =
        travelWalletItemsToApply.creditToApply?.amount.amount || 0;

      const walletItemCoverageChanged =
        (travelWalletItemsToApply.offerToApply ||
          travelWalletItemsToApply.creditToApply) &&
        Math.abs(offerToApplyAmount + creditToApplyAmount) >= predictedTotal &&
        Math.abs(offerToApplyAmount + creditToApplyAmount) < priceQuoteTotal;

      if (walletItemCoverageChanged) {
        return {
          title: travelWalletItemsToApply.creditToApply
            ? textConstants.PRICE_INCREASE_CREDITS_COVERAGE_TITLE
            : textConstants.PRICE_INCREASE_OFFER_COVERAGE_TITLE,
          subtitle: textConstants.PRICE_INCREASE_WALLET_COVERAGE_SUBTITLE,
          primaryButtonText,
          secondaryButtonText: textConstants.CHOOSE_ANOTHER_FLIGHT,
          icon: isIncrease
            ? textConstants.PRICE_INCREASE_ICON
            : textConstants.PRICE_DECREASE_ICON,
          type: ErrorModalType.FLIGHTS_PRICE_QUOTE_HAS_PRICE_DIFFERENCE,
        };
      }

      if (
        isIncrease &&
        hasAddOnBeenAttached &&
        flightBookType === FlightBookType.DEFAULT
      ) {
        return {
          title: textConstants.getPriceDifferenceTitle({
            isIncrease: true,
            amount: amountPerTraveler,
            currency,
            showPerTraveler: true,
          }),
          subtitle: textConstants.PRICE_INCREASE_WITH_ADD_ON_SUBTITLE,
          primaryButtonText,
          secondaryButtonText:
            flightBookType === FlightBookType.DEFAULT
              ? textConstants.CHOOSE_FLIGHT
              : undefined,
          icon: textConstants.PRICE_INCREASE_ICON,
          type: ErrorModalType.FLIGHTS_PRICE_QUOTE_WITH_ADD_ON_HAS_PRICE_DIFFERENCE,
        };
      }

      return {
        title: textConstants.getPriceDifferenceTitle({
          isIncrease,
          amount,
          currency,
        }),
        subtitle: isIncrease
          ? textConstants.PRICE_INCREASE_SUBTITLE
          : textConstants.PRICE_DECREASE_SUBTITLE,
        primaryButtonText,
        icon: isIncrease
          ? textConstants.PRICE_INCREASE_ICON
          : textConstants.PRICE_DECREASE_ICON,
        type: ErrorModalType.FLIGHTS_PRICE_QUOTE_HAS_PRICE_DIFFERENCE,
      };
    }

    if (schedulePaymentError) {
      return {
        title: textConstants.GENERIC_ERROR_TITLE,
        subtitle: textConstants.GENERIC_ERROR_SUBTITLE,
        icon: textConstants.ERROR_ICON,
      };
    }

    if (schedulePriceQuoteError) {
      return {
        title: textConstants.GENERIC_ERROR_TITLE,
        subtitle: textConstants.GENERIC_ERROR_SUBTITLE,
        icon: textConstants.ERROR_ICON,
      };
    }

    if (priceQuoteAndFinalizedError) {
      return priceQuoteAndFinalizedError;
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      const error = verifyPaymentMethodResult.toString();

      return {
        ...paymentTextConstants.PAYMENT_METHOD_ERROR_TITLES(
          error,
          accounts[0],
          isCorpTenant(config.TENANT)
        ),
      };
    }

    if (infantOnlyError) {
      return {
        title: textConstants.INVALID_PASSENGERS,
        subtitle: textConstants.INFANT_ONLY_SUBTITLE,
        icon: textConstants.ERROR_ICON,
        primaryButtonText: textConstants.UPDATE_TRAVELERS,
      };
    }

    switch (flightBookType) {
      case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
      case FlightBookType.DEFAULT:
        if (flightBookErrorTitle) {
          return flightBookErrorTitle;
        }
        break;
      default:
        break;
    }

    return {
      title: textConstants.GENERIC_ERROR_TITLE,
      subtitle: textConstants.GENERIC_ERROR_SUBTITLE,
      icon: textConstants.ERROR_ICON,
    };
  }
);

export const getModalEventType = createSelector(
  getPriceDifference,
  getPriceQuoteErrors,
  getFinalizedErrors,
  getPassengersValidErrors,
  getSchedulePaymentError,
  getSchedulePriceQuoteError,
  getTripPricingErrors,
  getVerifyPaymentMethodResult,
  // getIsConfirmAndBookReady,
  getFlightBookType,
  (
    getPriceDifference,
    priceQuoteErrors: (RedmondPurchaseError | FlightError)[],
    finalizedErrors: (RedmondPurchaseError | FlightError)[],
    passengerErrors: PassengerError[],
    schedulePaymentError,
    schedulePriceQuoteError,
    tripPricingErrors,
    verifyPaymentMethodResult,
    // isConfirmAndBookReady
    flightBookType
  ): string => {
    if (getPriceDifference.hasDifference && getPriceDifference.amount) {
      return "flight_price_difference";
    }

    if (schedulePaymentError) {
      return "schedule_payment_error";
    }

    if (schedulePriceQuoteError) {
      return "schedule_price_quote_error";
    }

    if (
      verifyPaymentMethodResult &&
      verifyPaymentMethodResult !== PaymentVerifyResultEnum.Success
    ) {
      return "payment_verification_failed";
    }

    if (finalizedErrors.length > 0) {
      return "poll_finalized_failed";
    }

    if (priceQuoteErrors.length > 0) {
      return "poll_price_quote_failed";
    }

    if (flightBookType === FlightBookType.DEFAULT) {
      if (passengerErrors.length > 0) {
        return "invalid_passengers";
      }

      if (tripPricingErrors.length > 0) {
        return "trip_pricing_failed";
      }
    }

    return "";
  }
);

export const getIsTripInternational = createSelector(
  flightsByFlightShopTypeSelector,
  selectedTripSelector,
  tripDetailsByIdSelector,
  (flights, selectedTrip, tripDetailsById) => {
    if (selectedTrip.tripId) {
      const tripDetails = tripDetailsById[selectedTrip.tripId];
      const airportMap = flights?.airports;
      const airportCodes = tripDetails.slices.flatMap((slice) => [
        slice.originCode,
        slice.destinationCode,
      ]);

      return airportCodes.some(
        (code) =>
          airportMap?.[code]?.geography.countryCode !==
          airportMap?.[airportCodes[0]]?.geography.countryCode
      );
    }
    return false;
  }
);

export const hasTravelFusionInSelectedTripDetailsSelector = createSelector(
  selectedTripDetailsSelector,
  (selectedTripDetails) => {
    if (!selectedTripDetails) {
      return false;
    }

    return containsTravelFusionFareSegmentBrand(
      selectedTripDetails.fareDetails
    );
  }
);

export const getIsTripVoidWindowEligible = createSelector(
  flightsByFlightShopTypeSelector,
  selectedTripDetailsSelector,
  hasTravelFusionInSelectedTripDetailsSelector,
  (flights, tripDetails, hasTravelFusionSegment) => {
    if (tripDetails) {
      const airportMap = flights?.airports;
      const airportCodes = tripDetails.slices.flatMap((slice) => [
        slice.originCode,
        slice.destinationCode,
      ]);

      const outgoingSlice = tripDetails.slices.find((slice) => slice.outgoing);

      const hasUSAirport = airportCodes.some(
        (code) => airportMap?.[code]?.geography.countryCode === "US"
      );
      const isWeekBeforeDeparture =
        dayjs(outgoingSlice?.departureTime).diff(new Date(), "days") >= 7;

      return hasUSAirport && isWeekBeforeDeparture && !hasTravelFusionSegment;
    }
    return false;
  }
);

export const getShopSummarySearchString = createSelector(
  getTripCategory,
  selectedTripSelector,
  tripDetailsByIdSelector,
  (tripCategory, selectedTrip, tripDetailsById) => {
    if (selectedTrip.tripId) {
      const tripDetails = tripDetailsById[selectedTrip.tripId];

      const isMulticity = tripCategory === TripCategory.MULTI_CITY;

      let queryParams = {};

      if (isMulticity && tripDetails) {
        const departureDetails = getMulticityParamsFromTripDetails(tripDetails);

        queryParams = {
          tripCategory,
          ...departureDetails,
        };
      }

      if (!isMulticity && tripDetails) {
        const departureSlice =
          tripDetails.slices[getSliceIndex(true, tripDetails)];
        const returnSlice =
          tripDetails.slices[getSliceIndex(false, tripDetails)];

        queryParams = {
          tripCategory,
          origin: departureSlice.originCode,
          destination: departureSlice.destinationCode,
          departureDate: dayjs(departureSlice.departureTime).format(
            "YYYY-MM-DD"
          ),
          returnDate:
            tripCategory === TripCategory.ROUND_TRIP && returnSlice
              ? dayjs(returnSlice.arrivalTime).format("YYYY-MM-DD")
              : undefined,
        };
      }
      return queryStringParser.stringify(queryParams);
    }
    return "";
  }
);

export const getPurchaseErrorFromLegacy = (
  error: LegacyPaymentError
): RedmondPurchaseError => {
  switch (error.PaymentError) {
    case PaymentErrorEnum.NoAvailability:
      return GeneralPurchaseErrorEnum.NoAvailability;
    case PaymentErrorEnum.FraudAutoReject:
      return GeneralPurchaseErrorEnum.FraudAutoReject;
    default:
      return {
        title: error.PaymentError,
        subtitle: error.code || error.message || error.msg || error.cause || "",
        code: error.code,
      } as UncategorizedPurchaseError;
  }
};

// parse errors and put into intermediate type
export const getPurchaseError = (
  error: PurchaseError
): RedmondPurchaseError | FlightError => {
  switch (error.Error) {
    case PurchaseErrorEnum.ErrorCode:
      const errorCode = error as ErrorCode;
      return {
        code: errorCode.code,
        title: errorCode?.code?.match(/[A-Z][a-z]+/g)?.join(" "),
        subtitle: errorCode.message || errorCode.cause || "",
      } as UncategorizedPurchaseError;
    case PurchaseErrorEnum.FraudAutoReject:
      return GeneralPurchaseErrorEnum.FraudAutoReject;
    case PurchaseErrorEnum.NoAvailability:
      return GeneralPurchaseErrorEnum.NoAvailability;
    case PurchaseErrorEnum.InActivity:
      return GeneralPurchaseErrorEnum.InActivity;
    case PurchaseErrorEnum.InvalidSession:
      return GeneralPurchaseErrorEnum.InvalidSession;
    case PurchaseErrorEnum.PaymentError:
      const paymentError = error as PaymentError;
      switch (paymentError.value.type) {
        case Payment.Rewards:
          const rewardsError =
            paymentError.value.value.RewardsError ||
            (paymentError.value.value as LegacyPaymentError).PaymentError;
          switch (rewardsError) {
            case PaymentErrorEnum.RedemptionFailure:
              return PaymentPurchaseErrorEnum.RedemptionFailure;
            default:
              return getPurchaseErrorFromLegacy(rewardsError);
          }
        case Payment.Card:
          const cardError =
            paymentError.value.value.CardError ||
            (paymentError.value.value as LegacyPaymentError).PaymentError;
          switch (cardError) {
            case PaymentErrorEnum.CardDeclined:
              // Check for backend-provided messages
              const cardDeclined = paymentError.value.value as CardDeclined;
              if (cardDeclined.messageTitle && cardDeclined.messageBody) {
                return {
                  code: PaymentErrorEnum.CardDeclined,
                  title: cardDeclined.messageTitle,
                  subtitle: cardDeclined.messageBody,
                } as UncategorizedPurchaseError;
              }
              return PaymentPurchaseErrorEnum.CardDeclined;
            default:
              return getPurchaseErrorFromLegacy(cardError);
          }
        default:
          throw new Error("Unknown Payment Error");
      }
    case PurchaseErrorEnum.ProductError:
      const purchaseError = error as ProductError;
      switch (purchaseError.value.type) {
        case Product.Flight:
          return purchaseError.value.value as FlightError;
        default:
          throw new Error("Unknown Product Error");
      }
    default:
      throw new Error(`Unexpected error type: ${error.Error}`);
  }
};

// copy of [[errorHelpers]] implementation but targetted towards flights
export const getAgentErrorTitleFromPurchaseError = (
  error: RedmondPurchaseError | FlightError
): string => {
  if ((error as UncategorizedPurchaseError).code) {
    return (error as UncategorizedPurchaseError).title;
  }
  switch (error) {
    case PaymentPurchaseErrorEnum.CardDeclined:
      return "Card Declined";
    case FlightError.DuplicateBooking:
      return "Duplicate Booking Found";
    case FlightError.InvalidPassengers:
    case FlightError.IllegalLapInfantKTN:
    case FlightError.IllegalLapInfantRedress:
    case FlightError.InvalidCustomer:
    case FlightError.InvalidPassengers:
    case FlightError.LapInfantTooOld:
    case FlightError.LapInfantsUnsupported:
    case FlightError.MalformedKnownTravelerNumber:
    case FlightError.MalformedRedressNumber:
    case FlightError.MissingPassport:
    case FlightError.NoAdultPassenger:
    case FlightError.NoContactInformation:
    case FlightError.SeatedInfantsUnsupported:
    case FlightError.TooManyLapInfants:
    case FlightError.TooManyPassengers:
      return "Invalid Passengers";
    case FlightError.ExpiredPassport:
      return "Passport Expired";
    case FlightError.MissingAirlineLocator:
      return "Locator Failed To Generate";
    case GeneralPurchaseErrorEnum.NoAvailability:
      return "No Availability";
    case FlightError.TransientServiceError:
      return "Transient GDS Error";
    case FlightError.UnknownSabreAppError:
      return "Sabre Error";
    case FlightError.CheckInMinimumAgeNotMet:
      return "The hotel did not accept your booking";
    case GeneralPurchaseErrorEnum.FraudAutoReject:
    case GeneralPurchaseErrorEnum.LikelyFraud:
      return "Likely Fraud (Do Not Inform Customer)";
    case PaymentPurchaseErrorEnum.RedemptionFailure:
      return "Issue Redeeming Rewards";
    default:
      return GENERIC_ERR_TITLE;
  }
};

export const getAgentErrorSubtitleFromPurchaseError = (
  error: RedmondPurchaseError | FlightError
): string => {
  if ((error as UncategorizedPurchaseError).code) {
    return (error as UncategorizedPurchaseError).subtitle;
  }
  switch (error) {
    case PaymentPurchaseErrorEnum.CardDeclined:
      return "Reconfirm that details are correct or try a different card.";
    case FlightError.DuplicateBooking:
      return "Identical booking found, confirm whether this booking is already completed.";
    case FlightError.InvalidPassengers:
      return "Confirm whether booking contains unsupported passengers, e.g. infants, solo young travelers.";
    case FlightError.MissingAirlineLocator:
      return "Inventory no longer available, try a different booking.";
    case GeneralPurchaseErrorEnum.NoAvailability:
      return "Inventory no longer available, try a different booking.";
    case FlightError.NoTicketlessResponse:
      return "This could be due to inventory no longer available. Please try again, if unsuccessful confirm availability.";
    case FlightError.ProviderError:
      return "This is typically an issue with Travelfusion";
    case FlightError.UnknownSabreAppError:
      return "Unknown Sabre issue, try again later.";
    case FlightError.CheckInMinimumAgeNotMet:
      return "The reservation holder did not meet the minimum age requirement of the hotel";
    case FlightError.ExpiredPassport:
      return "Please enter a valid passport to continue booking for this traveler.";
    case FlightError.IllegalLapInfantKTN:
      return "The lap infant's KTN is invalid";
    case FlightError.IllegalLapInfantRedress:
      return "The lap infant's Redress Number is invalid";
    case FlightError.InvalidCustomer:
      return "At least one adult traveler is required";
    case FlightError.LapInfantTooOld:
      return "The lap infant is too old to qualify";
    case FlightError.LapInfantsUnsupported:
      return "The lap infant is unsupported";
    case GeneralPurchaseErrorEnum.FraudAutoReject:
    case GeneralPurchaseErrorEnum.LikelyFraud:
      return "Advise Customer - There is an issue with you account, please contact Capital One Support";
    case FlightError.MalformedKnownTravelerNumber:
      return "A passenger's KTN is malformed";
    case FlightError.MalformedRedressNumber:
      return "A passenger's Redress Number is malformed";
    case FlightError.MissingPassport:
      return "Passport is missing";
    case FlightError.NoAdultPassenger:
      return "An adult passenger is required";
    case FlightError.NoContactInformation:
      return "Contact information is required";
    case PaymentPurchaseErrorEnum.RedemptionFailure:
      return "We were unable to redeem your rewards for this transaction";
    case FlightError.SeatedInfantsUnsupported:
      return "Seated infants are unsupported";
    case FlightError.TooManyLapInfants:
      return "Too many lap infants";
    case FlightError.TooManyPassengers:
      return "Too many passengers";
    default:
      return GENERIC_ERR_SUBTITLE;
  }
};

export const isPaymentCardHiddenSelector = createSelector(
  getIsChfarExercise,
  getFlightTotal,
  getChfarExerciseDiscountAmountFiat,
  (isChfarExercise, flightTotal, chfarExerciseDiscount) => {
    if (isChfarExercise && flightTotal && chfarExerciseDiscount?.value) {
      return flightTotal <= chfarExerciseDiscount.value;
    }
    return false;
  }
);

export const getSelectedOnlyNonVXCardPayment = createSelector(
  getPaymentsV2,
  getPaymentRequestType,
  getCardPaymentRewardsAccount,
  (paymentMethods, requestType, rewardsAccount) =>
    paymentMethods?.length === 1 &&
    requestType === PaymentSplitRequestEnum.PaymentCardRequest &&
    !rewardsAccount?.productDisplayName.includes("Venture X")
);

export const hasFintechAttached = createSelector(
  hasDisruptionProtectionBeenAttachedSelector,
  hasCfarBeenAttachedSelector,
  (hasFlightDisruption, hasCfar) => hasFlightDisruption || hasCfar
);
// Corporate Travel

export const getPolicyLimit = (state: IStoreState) =>
  state.flightShop.policyLimit;

// Recomputes `corporateTravel.policyCompliance` against the checkout-time,
// ancillary-inclusive total (`getFlightTotal`) rather than the stale
// shop-time value in `getCorporateTravel`. A fintech add-on or seat selected
// during checkout can push the total above `policyLimit` without the
// backend ever re-flagging the fare, which otherwise lets an out-of-policy
// trip through with no justification prompt.
export const getEffectiveCorporateTravel = createSelector(
  getCorporateTravel,
  getFlightTotal,
  getPolicyLimit,
  (corporateTravel, totalWithAncillaries, policyLimit) =>
    getEffectiveCorporateTravelPure({
      corporateTravel,
      totalWithAncillaries,
      limit: policyLimit?.value,
    })
);

export const getApprovalRequestReason = (state: IStoreState) =>
  state.flightBook.approvalRequestReason;

export const getTripDescription = (state: IStoreState) =>
  state.flightBook.tripDescription;
